import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Footer } from 'components/Footer';
import { AuthenticChip, LogoLoader, TermInfo } from 'components/UI';
import { photoScrimSx } from 'components/pages/certificates/productPage/photoScrimSx';
import { CONTENT_MAX_WIDTH, PAGE_GUTTER } from 'constants/layout';
import { BlockWithBorder } from 'components/UI/BlockWithBorder';
import { DrawerPopup } from 'components/UI/DrawerPopup';
import { ImageCarousel } from 'components/UI/ImageCarousel';
import { AboutProduct } from 'pages/CertificatePage/components/AboutProduct';
import { PreviouslyOwnedCard } from 'pages/CertificatePage/components/PreviouslyOwnedCard';
import {
  useProvenanceConsent,
  useUpdateProvenanceConsent,
} from 'hooks/api/useProvenanceConsent';
import {
  NavigationHeader,
  NAV_HEADER_HEIGHT,
} from 'components/pages/certificates/productPage/NavigationHeader';
import { useScrolledPast } from 'hooks/useScrolledPast';
import { FieldRow } from 'components/Fields/FieldRow';
import { font, space } from 'settings/tokens';
import { containerRadius, innerRadius } from 'settings/brandRadius';
import { certificateScrollKey } from 'pages/CertificatePage/subviews/CertificateSubView';
import { ClaimScanProcess } from 'components/pages/certificates/productPage/ClaimScanProcess';
import { HowToScanBlock } from 'components/pages/certificates/productPage/HowToScanBlock';
import { OwnerBlock } from 'components/pages/certificates/productPage/OwnerBlock';
import { ProductPageHeader } from 'components/pages/certificates/productPage/ProductPageHeader';
import {
  CertificateBrandHeader,
  hasCertificateBrandHeader,
} from 'components/pages/certificates/productPage/CertificateBrandHeader';
import { CertificatePageSkeleton } from 'components/pages/certificates/productPage/CertificatePageSkeleton';
import { ScanClaimProcess } from 'components/pages/certificates/productPage/ScanClaimProcess';
import { ScanHistory } from 'components/pages/certificates/productPage/ScanHistory';
import { SuccessOwnership } from 'components/pages/certificates/productPage/SuccessOwnership';
import { TransferOwnership } from 'components/pages/certificates/productPage/TransferOwnership';
import { VideoPlayer } from 'components/pages/certificates/productPage/VideoPlayer';
// VRG-790 — the post-verify reveal: the pre-flight, the player, and the per-viewer
// "already seen" record that keeps it to one play per certificate.
import { RevealScreen } from 'components/pages/certificates/productPage/RevealScreen';
import { useCertificateReveal } from 'hooks/api/useCertificateReveal';
import { hasSeenReveal, markRevealSeen, prefersReducedMotion } from 'utils/revealSeen';
import { LinkAccounts } from 'components/pages/signIn/LinkAccounts';
import { SignIn } from 'pages/SignIn';

import { accountLinkCheck } from 'store/auth/actions';
import { changeCertificateStatusByCustomer } from 'store/certificates/actions';
import { useDispatch, useSelector } from 'store/hooks';
import { DemoBanner } from 'components/UI/DemoBanner';
import { isDemoHost } from 'utils/subdomain';
import { formatedDate } from 'utils/date';
import { getOwner, isEmailValid } from 'utils/parsers';
import { slideInSx, slideOutSx, shouldSlide, pushInSx, shouldPushDesktop, PAGE_SLIDE_MS, type SlideNavState } from 'utils/pageTransition';
import { EXPERIENCE_SECTION_ENABLED, certificateSubViewSections } from './certificateSections';
import { customFieldHtmlHasText } from 'utils/certificateHtml';
import { ExperienceTabContent } from './components/certificateTabs/tabs/ExperienceTabContent';
import { isCertificateOwnedBy } from 'utils/certificateUtils';
import {
  clearScrollPosition,
  getScrollPosition,
  saveScrollPosition,
  scrollToTop,
} from 'utils/scrollRestore';

/*
 * The certificate's sections — the spine plus whatever the brand has added — now come
 * from `certificateSections`, not from a const in this file (VRG-782).
 *
 * The const that stood here held the three sub-view rows and their glyphs. Moving it
 * out is what makes "adding a section requires no edit to the certificate page" true:
 * this page maps over whatever the registry returns, and the registry spreads the
 * four-section spine ahead of the brand's own at a single site, so nothing can
 * displace or reorder it. The reasoning for the glyph choices and for which rows carry
 * a glossary `(i)` moved with it.
 */
/**
 * VERA-315 — the DESKTOP (md+) two-column split.
 *
 * ## What was wrong
 *
 * This page was a single vertical column with no `maxWidth`, i.e. a phone layout
 * rendered at whatever width the window happened to be. Measured at 1280×800 the
 * `ImageCarousel` (`fit='cover'`, and an inner `h-[100vw]`) rendered **1280×1280**,
 * so the ENTIRE record — title, status, transfer summary, facts, History, Manage,
 * the public sections — began at y≈1296 on an 800px viewport. You scrolled past a
 * full window of photograph to reach the first fact about the object.
 *
 * ## The split
 *
 * LEFT = the media, STICKY. The photograph is the identity anchor: it says *which*
 * object this is and does not change as you read. RIGHT = the record, scrolling,
 * because the record is the thing with length. Sticky rather than fixed for the
 * same reason `NavigationHeader` is (see its note): sticky is sized by its own
 * containing block and survives the page-transition `transform` that would reparent
 * a fixed child's containing block.
 *
 * 5/7, not 6/6 — the record column carries denser content (multi-line facts, rows,
 * badges) and the media is square, so an equal split leaves the media oversized and
 * the text measure cramped at the same time.
 *
 * CONTAINED at `CONTENT_MAX_WIDTH` and centred, not edge-to-edge — a cap, so the
 * page does not run across a 2560px monitor, but the SAME cap every other page takes.
 *
 * ⚠️ It was 1120 until 2026-08-24, on a "reading width" argument: that this page is a
 * record you read, and prose past a certain measure is unreadable. Measuring it did
 * not support the number. This page's one prose element is the product Description
 * (`ProductTabContent`), which fills the record column, and at the 1120 cap that
 * column was 602px — 86 characters per line at `font.body` (16px Roboto, measured at
 * 7.03px/char), already past the 45–75 the argument appeals to. So the cap was not
 * delivering a reading measure; it was costing the PHOTOGRAPH 112px to fail at one.
 * The honest instrument for prose is a measure on the prose (cf. `PROSE_MAX_WIDTH`),
 * not on the page container. Full reasoning at the top of `constants/layout`.
 *
 * ⚠️ THE WIDTH AND THE 24 ARE NOT THIS PAGE'S TO CHOOSE. They were declared
 * here, privately, and that is exactly how the app ended up with four different
 * content widths — every other page had to invent its own rather than import a
 * constant from a page. They now live in `constants/layout` as `CONTENT_MAX_WIDTH` /
 * `PAGE_GUTTER` and are shared with `components/UI/PageContainer`, which is what
 * the certificates list, the sub-views and the rest of the app use to land on the
 * same column. Change them THERE; do not re-localise them here.
 *
 * ## Mobile is untouched, deliberately
 *
 * `apps/app` is reached by TAPPING AN NFC TAG WITH A PHONE (working-conventions
 * §37) — the phone IS the product and desktop is the secondary case. So every rule
 * here is inside a `breakpoints.up('md')` block and the base styles reproduce the
 * previous flex column exactly: the two new wrapper elements are `display: flex;
 * flex-direction: column; flex: 1 1 auto`, which is what the outer `Stack` already
 * was, so the `flex: 1 1 auto` chain that pushes the Footer down is preserved and
 * adjacent-sibling margins keep the same (non-collapsing) behaviour they had.
 */
/* Aliases, not values — the numbers are `constants/layout`'s (see the note above).
   Kept as local names only so the ~6 references below and `NavigationHeader`'s two
   props read the same as they did. */
const DESKTOP_MAX_WIDTH = CONTENT_MAX_WIDTH;
const DESKTOP_GUTTER = PAGE_GUTTER;
const DESKTOP_COLUMN_GAP = 40;
/** The page's top inset, which the sticky media column also stops against. */
const DESKTOP_TOP = NAV_HEADER_HEIGHT + DESKTOP_GUTTER;

/**
 * The two-column container. `flex: 1 1 auto` applies to it as an ITEM of the outer
 * Stack regardless of its own `display`, so switching to `grid` at md does not break
 * the "footer sits at the bottom of a short page" chain.
 */
const pageBodySx = (hasBrandHeader: boolean) => (theme: Theme) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minWidth: 0,
  [theme.breakpoints.up('md')]: {
    display: 'grid',
    // `minmax(0, …)` on both tracks: an `fr` track's default `min-width: auto` is
    // its content's min-content size, so a long unbroken string (a SKU, a URL in
    // the description) would otherwise widen the track past its share.
    gridTemplateColumns: 'minmax(0, 5fr) minmax(0, 7fr)',
    columnGap: `${DESKTOP_COLUMN_GAP}px`,
    // Required for the sticky media column: a stretched grid item fills its row and
    // has no room left to travel within, so `position: sticky` would never engage.
    alignItems: 'start',
    width: '100%',
    maxWidth: `${DESKTOP_MAX_WIDTH}px`,
    mx: 'auto',
    px: `${DESKTOP_GUTTER}px`,
    // `NavigationHeader` is in flow with `margin-bottom: -NAV_HEADER_HEIGHT` so the
    // photograph can start at the very top of the page on a phone. This adds the bar
    // back plus a gutter, so on desktop the bar reads as a bar with content beneath.
    //
    // `CertificateBrandHeader` is the other occupant of that band, on the public
    // view where `NavigationHeader` never renders — but unlike the nav bar it is
    // SOLID and in flow, so it contributes its own `NAV_HEADER_HEIGHT` rather than
    // cancelling it. Reserving the full `DESKTOP_TOP` on top of that would spend
    // the band twice and leave a bar-height hole beneath the mark; the gutter is
    // all that is still owed.
    pt: `${hasBrandHeader ? DESKTOP_GUTTER : DESKTOP_TOP}px`,
    pb: `${DESKTOP_GUTTER * 2}px`,
  },
});

/**
 * The media column. The no-media header clearance, and the TWO separate things
 * that discharge it.
 *
 * The reservation exists because a transparent, negatively-margined
 * `NavigationHeader` would otherwise land its controls on the placeholder. Either
 * flag answers that:
 *
 * - `hasHeroMedia` — there is full-bleed media for the header to sit over. VRG-749
 *   renamed this from `hasImages`, and the rename is the fix rather than a tidy-up:
 *   a 3D model answers "is there hero media" just as a photograph does, so left
 *   reading only `images`, a product with a model and no photos — exactly the shape
 *   of the first one, since a model is what REPLACES its photography — would take
 *   the no-media clearance and push its own hero down by the header's height for
 *   no reason. Nothing would have failed to compile.
 * - `hasBrandHeader` — a solid in-flow brand bar has already pushed the column
 *   clear of it, so reserving on top of a bar that is really there is just a second
 *   empty 72px.
 *
 * ⚠️ Both, not either/or. They arrived from different branches and each was written
 * as the whole condition; keeping one would have reintroduced the other's bug.
 */
const mediaColSx = (hasHeroMedia: boolean, hasBrandHeader: boolean) => (theme: Theme) => ({
  position: 'relative' as const,
  pt: hasHeroMedia || hasBrandHeader ? 0 : `${NAV_HEADER_HEIGHT}px`,
  [theme.breakpoints.up('md')]: {
    position: 'sticky' as const,
    top: `${DESKTOP_TOP}px`,
    alignSelf: 'start',
    // The container's own `pt` already clears the bar here.
    pt: 0,
    minWidth: 0,
  },
});

/**
 * The photograph's desktop frame.
 *
 * `ImageCarousel` sizes itself with `h-[100vw]` on both the carousel root and the
 * `<img>` — "square at the phone's width", which is right at 375 and is 1280px of
 * photograph at 1280. Rather than change a component eight other surfaces render,
 * the frame states a definite height (`aspect-ratio: 1/1` on a column-width box) and
 * hands it down the carousel's own DOM chain. `!important` because the tailwind
 * utility and these rules are both author-origin and the utility is on the element.
 */
/**
 * The photograph AND the chip that floats on it, as one box.
 *
 * It was introduced so the chip had a containing block that ENDS AT THE MEDIA: the
 * Manage actions used to hang under the photograph inside this column at md+, and
 * the chip wrapper is `position: absolute`, so anchored to the COLUMN it would have
 * slid down past the photograph and sat over those buttons.
 *
 * That original reason is gone — Manage is in the header's kebab sheet now (see
 * `manageItems`) and this column holds only the photograph. The box stays anyway,
 * because scoping the chip to the photograph rather than to the column is correct
 * regardless of what else the column holds, so the next thing placed here does not
 * re-break it.
 *
 * ── NO BOTTOM PADDING, and the chip's offset absorbed the difference ─────────────
 * It used to carry `pb: 16px`. At 375 the record column follows this one, so that
 * padding WAS the white gap between the photograph and the demo disclosure bar
 * (`DemoBanner`), which then sat immediately below the image — the bar is full-bleed
 * and unmargined, so nothing else separated them. It was removed so the two read as
 * attached.
 *
 * VRG-825 — THAT BAR NO LONGER SITS HERE. It moved above `CertificateBrandHeader`,
 * so nothing is flush under the photograph any more and the original reason is
 * history. The padding STAYS removed anyway, on a different and now simpler ground:
 * with the bar gone, every page — demo and not — runs photograph → title, and that
 * gap is the single 32px visual value the title block owns (see its note below).
 * Restoring 16 here would put demo pages back at 38 and re-split a gap that is now
 * one number.
 *
 * THE CHIP MUST NOT MOVE, and it is anchored to THIS box, so removing the padding
 * alone would have dragged it up by exactly the 16px removed. The wrapper's `bottom`
 * went 32 → 16 in the same edit to cancel that: the chip's bottom edge sits 16px
 * above the photograph's bottom edge either way. That is the whole reason this is a
 * two-sided edit — change one side alone and the chip moves.
 *
 * Measured at 375 and 1280, before and after: photograph bottom 375.00 unchanged,
 * chip 329.09 → 359.00 unchanged, banner top 391.00 → 375.00. At md+ the banner is
 * in the OTHER grid track (level with the top of the photograph, not under it), so
 * there is no gap there to close and nothing there to go flush against; the only
 * effect at that width is that this sticky column is 16px shorter.
 */
const mediaShotSx = { position: 'relative' as const };

const mediaFrameSx = (theme: Theme) => ({
  /*
   * The bottom scrim + `position: relative` now live in `photoScrimSx`, shared
   * with VRG-455's pending-sale page — the second surface to float
   * `AuthenticChip` on a product photograph (§36). The full reasoning (the
   * measured 1.41:1 on the pale Cartier shot, and why a heavier chip cannot fix
   * it) moved there with the values.
   */
  ...photoScrimSx,
  /*
   * VRG-749 follow-up — ONE plate, serving the model and the still photo.
   *
   * It sits here rather than inside `ProductModel` deliberately: this frame holds
   * BOTH branches of the hero, so a transparent PNG and a 3D model land on exactly
   * the same surface. Painting it in the viewer instead would give the model a
   * plate and the photograph the page, which is the inconsistency worth avoiding —
   * and `ProductModel`'s `bare` surface means precisely "the caller owns this".
   *
   * The card surface, from the same helper the platform uses. Note that on THIS app
   * the mode is not a user preference: `createMuiTheme` never sets `palette.mode`,
   * so it is always nominally light, and what moves the page is the BRAND, via
   * Studio's `bg_color` resolving into `background.default`
   * (`settings/mui/palette.ts`). `background.paper` is not themed from Studio today,
   * so on a brand that sets a dark page this plate stays light — which is the right
   * outcome for the artwork and is NOT a decision this file is making; it is what
   * the app's own card surface already does.
   *
   * Invisible for the common case, by construction: `ImageCarousel` is `cover`, so
   * an opaque photograph fills the frame and nothing of the plate shows. It is only
   * ever seen through artwork that has transparency — which is the ask.
   */
  backgroundColor: mediaPlateColor(theme),
  [theme.breakpoints.up('md')]: {
    aspectRatio: '1 / 1',
    overflow: 'hidden',
    // VRG-562: the hero photo is the largest surface on the page and the first
    // thing a brand looks at — container step, 16px at the default as before.
    borderRadius: containerRadius(theme),
    '& > div': { height: '100% !important' },
    '& .carousel-root, & .carousel-slider, & .slider-wrapper, & .slider, & .slide, & .slide > div':
      { height: '100% !important' },
    '& .carousel img': { height: '100% !important', width: '100%', objectFit: 'cover' },
  },
});

/** The record column — a plain flex column at every width; the grid places it. */
const recordColSx = {
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minWidth: 0,
};

import {
  readAccessConsent,
  getLocationIfAlreadyPermitted,
  cachedAccessSatisfies,
} from 'utils/accessConsent';

import { useAuth, useDeferredLoading } from 'hooks';
import { ContentFade } from 'components/UI/ContentFade';
import { useTransferCertificate } from 'hooks/api/useTransferCertificate';
import { useScanLogs } from 'hooks/api/useScanLogs';
// VRG-778 — the PUBLIC share affordance. `shareLink` keeps the native-sheet call
// inside the user's gesture (see its docblock: an await before it loses transient
// activation and the call rejects on iOS only).
import {
  useRecordCertificateShare,
  useRecordCertificateShareVisit,
} from 'hooks/api/useCertificateShareEvents';
import { mintShareToken, shareLink } from 'utils/shareLink';
import { useSnackbar } from 'components/Snackbar';
import { certificateActivityQuery } from 'hooks/api/useCertificateActivity';
import { ACTIVITY_PAGE_SIZE } from 'pages/CertificatePage/components/certificateTabs/tabs/ActivityTabContent';
import { ProductModel, isInvalidScan, getScanLocationLabel, mediaPlateColor } from '@repo/ui';
// `History`, `MapPin` and `Users` are NOT imported here any more (VRG-782) — the row
// glyphs moved into `certificateSections.ts` with the section registry. The remaining
// matches for those words in this file are prose, not components; re-adding them to
// this import is how the registry quietly stops being the only place rows are defined.
import { ShieldAlert, UserRoundMinus } from 'lucide-react';

import {
  Button,
  Stack,
  Typography,
  Box,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import type { Theme } from '@mui/material';
import { SectionLabel } from 'components/UI/SectionLabel';
import { StatusBadge } from 'components/pages/certificates/StatusBadge';
import { EmailPopup } from 'components/UI/EmailPopup';
import { ActionMenu } from 'components/UI/ActionMenu';
import { buildCertificateActionItems } from 'components/pages/certificates/certificateActionItems';
import type { CertificateActionItem } from 'components/pages/certificates/certificateActionItems';
import { ActionTileRow } from './components/ActionTileRow';
import {
  Eye,
  Gift,
  OctagonAlert,
  BadgeCheck,
  Share2,
  ExternalLink,
  Settings,
} from 'lucide-react';
import { ReportMissingDrawer } from 'components/pages/certificates/productPage/ReportMissingDrawer';
import { GiftCertificate } from 'components/pages/certificates/productPage/GiftCertificate';
import { SellCertificate } from 'components/pages/certificates/productPage/SellCertificate';
import { TransferCompletionMethod } from '@repo/types';
import { ScanTag } from 'components/pages/certificates/productPage/ScanTag';
import {
  useVerifyPossession,
  mintDevScanToken,
} from 'hooks/api/transfers/useVerifyPossession';
import { useQueryClient } from '@tanstack/react-query';
import { useGetCertificatePublic } from 'hooks/api/useGetCertificatePublic';
import {
  useCertificateAccessSettings,
  type ResolvedCertificateAccess,
} from 'hooks/api/useCertificateAccessSettings';
import { useCertificateViews } from 'hooks/api/useCertificateViews';
import { useScanVerification, type ScanVerificationState } from 'hooks/api/useScanVerification';
import { createApiCall } from 'services';
import {
  CARD_HEADING,
  TransferSummarySection,
  useTransferCardState,
} from 'components/pages/certificates/productPage/transferSummary/tranferSummary';
import { NotFound } from 'pages/NotFound';
import { RequestAccessInfo } from './components/RequestAccessInfo';
import { ClaimOnScanCard } from './components/ClaimOnScanCard';
import { GateSurface } from './components/GateSurface';
import type { ClaimOnScanFields } from 'hooks/api/claimOnScan';
import { GatedDetailsPlaceholder } from './components/GatedDetailsPlaceholder';
import { ProductDetailsPanel } from 'pages/CertificatePage/components/ProductDetailsPanel';
import { ProductTabContent } from './components/certificateTabs/tabs/ProductTabContent';
import { PublicCertificateSections } from './components/PublicCertificateSections';
import { ContactCardSection } from './components/ContactCardSection';
import { storeContactCard } from 'hooks/api/useContactCard';
import { PreviewContext } from 'contexts/previewContext';
import { OwnerPublicPreviewHeader } from './components/OwnerPublicPreviewHeader';
import { ViewChoiceSheet } from './components/ViewChoiceSheet';
import { MissingStateCallout } from './components/MissingStateCallout';
import { SuspiciousActivityCard } from './components/SuspiciousActivityCard';
import { VerifyPossessionCard } from './components/VerifyPossessionCard';
import {
  STATE_SEVERITY,
  StateCardStack,
  type StateCardItem,
} from './components/StateCardStack';
import { SealOpenedDrawer } from './components/SealOpenedDrawer';
import { AuthenticityDrawer } from 'components/pages/certificates/AuthenticityDrawer';
import { TagOpenedBadge } from 'components/pages/certificates/TagOpenedBadge';

export enum ViewTypes {
  OWNER = 'owner',
  OWNER_PUBLIC_PREVIEW = 'owner_public_preview',
  PUBLIC = 'public',
}

export const CertificatePage = () => {
  const [openHowToScan, setOpenHowToScan] = useState<boolean>(false);
  const [signIn, setSignIn] = useState<boolean>(false);
  const [linkAccounts, setLinkAccounts] = useState<boolean>(true);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [openVideoGuide, setOpenVideoGuide] = useState<boolean>(false);
  const [openAcceptTransfer, setOpenAcceptTransfer] = useState<boolean>(false);
  const [claimedProduct, setClaimedProduct] = useState<boolean>(true);
  const [isShowSuccessOwnership, setIsShowSuccessOwnership] = useState<boolean>(false);
  const [openTransferOwnership, setOpenTransferOwnership] = useState<boolean>(false);
  const [openOptions, setOpenOptions] = useState<boolean>(false);
  const [openReportMissing, setOpenReportMissing] = useState<boolean>(false);
  const [openGiftCertificate, setOpenGiftCertificate] = useState<boolean>(false);
  /*
    The Sell sheet. `SellCertificate` owns its own step machine, so this page
    holds only whether the sheet is open — remounting on open is what resets it
    to the method chooser (see `key` on the DrawerPopup child below).
  */
  const [openSellMethod, setOpenSellMethod] = useState<boolean>(false);
  /** Bumped on each open, purely to remount the sheet so it reopens at step one. */
  const [sellOpenCount, setSellOpenCount] = useState<number>(0);
  const [openVerifyPossession, setOpenVerifyPossession] = useState<boolean>(false);
  const [openSealOpened, setOpenSealOpened] = useState<boolean>(false);
  const [openAuthenticity, setOpenAuthenticity] = useState<boolean>(false);
  const [openConfirmStatusChange, setOpenConfirmStatusChange] = useState<boolean>(false);
  const [viewType, setViewType] = useState<ViewTypes>(ViewTypes.PUBLIC);
  /*
   * VRG-781 — the per-SCAN arrival choice.
   *
   * `useState`, with nothing persisted anywhere, IS the "per scan" decision rather than
   * a shortcut: the answer is a function of the situation (checking your own item vs.
   * showing it to someone), so an owner alternates between the two unpredictably and any
   * remembered answer would be wrong about half the time by construction. See
   * ViewChoiceSheet for the full argument and for the two alternatives that were
   * rejected — a remembered preference and a persistent toggle in the chrome.
   *
   * Starts false and is opened by the effect below, so it can only ever appear once the
   * ownership check has actually resolved.
   */
  const [viewChoiceAsked, setViewChoiceAsked] = useState<boolean>(false);
  const [openViewChoice, setOpenViewChoice] = useState<boolean>(false);
  // Seeded from the stored per-origin consent, so a visitor who has already given their
  // email on this brand's domain is not re-asked. This was a bare `useState(false)`, which
  // meant the gate reappeared on EVERY mount — even a refresh of the same certificate in
  // the same tab. A lazy initialiser (not an effect) so the gate never flashes before the
  // stored value is read. See utils/accessConsent.ts for scope and TTL.
  const [isAccessDataCollected, setIsAccessDataCollected] = useState<boolean>(
    () => readAccessConsent() !== null,
  );
  /**
   * VRG-591 — does that stored consent still COVER the gate as it is configured now?
   *
   * The seed above is deliberately optimistic and deliberately synchronous: it has to run
   * before first paint, and at that point neither the certificate nor its access settings
   * have loaded, so there is nothing to compare the consent against. This is the
   * correction, applied once the settings resolve.
   *
   *  - `none`         no consent stored. Nothing to re-check.
   *  - `pending`      one is stored and the requirement is not known yet.
   *  - `ok`           it covers the requirement. Carry on.
   *  - `insufficient` it does not — `collect_location` became a hard gate, or the visitor
   *                   revoked the browser grant the stored consent was given under. The
   *                   gate applies again.
   *
   * Without this, a 180-day cached consent is a 180-day bypass of a requirement that did
   * not exist when it was written.
   */
  const [cachedConsentVerdict, setCachedConsentVerdict] = useState<
    'none' | 'pending' | 'ok' | 'insufficient'
  >(() => (readAccessConsent() ? 'pending' : 'none'));

  // VRG-720 slice 2: "View this item" on the claim card hands the page back to the ordinary
  // access flow for a visitor who does not want to claim.
  const [claimDismissed, setClaimDismissed] = useState(false);

  // VERA-644 (TD-066 last-mile): in preview mode, the Studio postMessage
  // channel's live `hidePoweredBy` overrides the mock certificate's (absent)
  // field so the WhiteLabelCard's local toggle reflects immediately.
  const { isPreview, hidePoweredBy: previewHidePoweredBy, previewPublicView, previewEmbedded } = useContext(PreviewContext);
  const dispatch = useDispatch();
  const { certificateId } = useParams();
  // VERA-455: only assert "Authentic" when a valid scan-verification token proves
  // this render came from a genuine physical tag scan.
  const [searchParams] = useSearchParams();
  const scanVerified = useScanVerification(certificateId, searchParams.get('vt'));

  // VRG-309. The hostname half of the demo disclosure. Deliberately NOT derived from the API
  // response — see utils/subdomain.ts `isDemoHost`: the lookup that supplies the brand name
  // swallows its own failures, so keying the disclosure off it would let a failed request
  // SUPPRESS it. The second half is computed below, once `certificate` exists, and it can only
  // ever ADD a disclosure — never remove one.
  const isDemoHostname = isDemoHost();

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  // `useLocation` was imported but never called, so a bare `location` in this file
  // resolved to `window.location` — which has no `.state`, so the read was a silent
  // `undefined` rather than an error.
  const location = useLocation();

  /**
   * Open at the top — unless we are coming BACK from one of this certificate's
   * sub-views, in which case return to where the row was.
   *
   * The app scrolls the document, so arriving from a scrolled list leaves the
   * new page at that same offset — tapping the ninth card opened its
   * certificate already scrolled past the photograph. Keyed on the id so moving
   * between certificates resets too, not just the first mount.
   *
   * VERA-315 Phase 4 adds the second case. The Provenance / Scans / Activity
   * rows sit below the fold, so landing back at the top after closing one would
   * lose the reader's place in exactly the way the list → certificate
   * restoration exists to prevent. Same mechanism, same module — the position is
   * consumed once and cleared, so a later fresh visit still opens at the top.
   */
  useLayoutEffect(() => {
    const saved = getScrollPosition(certificateScrollKey(certificateId));
    if (saved === undefined) {
      scrollToTop();
      return;
    }
    clearScrollPosition(certificateScrollKey(certificateId));
    window.scrollTo({ top: saved, behavior: 'auto' });
  }, [certificateId]);

  /**
   * Push one of the certificate's sub-views, remembering where we were so the
   * back arrow can put us back.
   *
   * `extraState` is how a caller names WHAT it is sending the user to look at —
   * today only `focusScanId`, so the suspicious-activity card can open the Scans
   * map on the offending scan rather than on the whole spread. It goes through
   * router state alongside `slideIn` because this app runs a `MemoryRouter` (see
   * `handleConfirmStatusChange`): there is no address bar, so a query param would
   * have nowhere to live and nothing to be shared from.
   */
  const openSubView = (path: string, extraState?: Record<string, unknown>) => {
    saveScrollPosition(certificateScrollKey(certificateId));
    navigate(`/certificates/${certificateId}/${path}`, {
      state: { slideIn: shouldSlide(), ...extraState },
    });
  };
  const queryClient = useQueryClient();

  let { shopify_user, linkedAccounts, profile } = useSelector(({ auth }) => ({
    shopify_user: auth.shopify_user,
    profile: auth.profile,
    linkedAccounts: auth.isAccountLinked,
  }));

  const {
    data: certificate,
    isLoading: isLoadingCertificate,
    isPlaceholderData: isCertificatePlaceholder,
    isError: isCertificateError,
    error: certificateError,
  } = useGetCertificatePublic(certificateId as string);

  /*
   * VRG-790 — the reveal.
   *
   * ## Fired HERE, beside the certificate query, so the two fly together
   *
   * The reveal exists to cover this page's load. Its media lives on the product, which
   * arrives INSIDE the certificate payload — so a player that waited for that payload
   * could only start once the thing it was meant to mask had already landed, and the
   * reveal would be added latency rather than a cover. Hence the dedicated pre-flight
   * route, fetched in parallel: the cost is max(a, b) rather than a + b, the same
   * reasoning the scan-logs query below records, and the tiny response reliably wins.
   */
  const { data: reveal } = useCertificateReveal(certificateId);

  /**
   * `deciding` -> `playing` -> `closed`, and `closed` is terminal.
   *
   * A three-state machine rather than a boolean because "we do not know yet" has to be
   * distinguishable from "no". A boolean would read as "no reveal" on the first render
   * and skip every reveal there has ever been, while the query it was waiting for
   * resolved correctly a moment later (the same trap the hook's missing `placeholderData`
   * documents, one layer up).
   */
  const [revealPhase, setRevealPhase] = useState<'deciding' | 'playing' | 'closed'>(
    'deciding',
  );

  /**
   * Reasons never to play, resolved once per certificate.
   *
   * `prefers-reduced-motion` is a request to be taken to the content, and the content is
   * the certificate — so the reveal is not shortened or replaced by a held still, it is
   * not mounted. `hasSeenReveal` is the collector case: twenty taps in an afternoon must
   * not mean twenty animations.
   *
   * Read through `useMemo` on the id rather than in the effect below, so a re-render
   * cannot re-ask localStorage on every pass.
   */
  const revealSuppressed = useMemo(
    () => prefersReducedMotion() || hasSeenReveal(certificateId),
    [certificateId],
  );

  /**
   * THE RACE, and it is the rule that makes "the reveal never blocks" true.
   *
   * A reveal may only start while the certificate is still in flight. If the certificate
   * lands first there is nothing left to cover, so the window closes and the viewer goes
   * straight through — a reveal that popped up after the page had painted would slam a
   * black overlay over a certificate someone was already reading.
   *
   * `certificate` arriving as `placeholderData` (a warm navigation from the list) counts
   * as ready, which is correct twice over: there is no load to cover, and the viewer has
   * just been looking at that certificate's row.
   */
  const isCertificateReady = Boolean(certificate) && !isLoadingCertificate;

  useEffect(() => {
    if (revealPhase !== 'deciding') return;

    if (revealSuppressed) {
      setRevealPhase('closed');
      return;
    }
    // The pre-flight has answered yes. Both URLs are guaranteed present by the API's
    // `resolveReveal`, and re-checked here because a cached payload from before the
    // field existed would otherwise reach `RevealScreen` with a null source.
    if (reveal?.enabled && reveal.video_url && reveal.poster_url) {
      setRevealPhase('playing');
      return;
    }
    // Answered, and the answer is no.
    if (reveal) {
      setRevealPhase('closed');
      return;
    }
    // Not answered yet. Closed only if the certificate beat it — see THE RACE above.
    if (isCertificateReady) setRevealPhase('closed');
  }, [reveal, revealSuppressed, isCertificateReady, revealPhase]);

  const finishReveal = useCallback(() => setRevealPhase('closed'), []);
  const recordRevealSeen = useCallback(
    () => markRevealSeen(certificateId),
    [certificateId],
  );

  /**
   * Rendered in EVERY branch below — over the skeleton, over the grace-period null, and
   * over the loaded page — because the reveal is a fixed full-screen overlay and which
   * of those three is underneath it is none of its business. Leaving it out of one branch
   * would make the reveal vanish the moment that branch was taken, mid-animation.
   */
  const revealOverlay =
    revealPhase === 'playing' && reveal?.video_url && reveal?.poster_url ? (
      <RevealScreen
        videoUrl={reveal.video_url}
        posterUrl={reveal.poster_url}
        onDone={finishReveal}
        onSeen={recordRevealSeen}
      />
    ) : null;

  /**
   * The brand's Settings → Features → "Certificate access" toggles, which decide
   * whether this visitor may be asked for an email or a location at all.
   *
   * Both were WRITE-ONLY before this: a brand could switch them off and every
   * visitor was still asked. The gate below was `viewType === PUBLIC &&
   * !isAccessDataCollected` with no reference to the company's settings.
   *
   * ⚠️ Keyed on `certificate.company_id`, NOT on `CompanyContext`. The context id
   * comes from the subdomain and falls back to a hardcoded company when there is
   * none — every localhost page, every embed, and every brand that has moved to
   * its own domain post-takeover — so reading the flags off it would apply the
   * wrong brand's privacy setting. `undefined` while the certificate is still
   * loading is deliberate and distinct from `null`; see the hook's table.
   */
  const accessSettings = useCertificateAccessSettings(
    certificate ? certificate.company_id ?? null : undefined,
    /*
     * VRG-591 — the pair the SERVER already resolved for this certificate, down
     * `certificate ?? product ?? company ?? default`. It outranks the company row, which
     * is all this hook used to read: a per-product or per-certificate override was
     * rendered in the platform and ENFORCED on the access endpoint, but invisible here,
     * so an opt-out still asked the visitor and then 403'd them on submit.
     */
    (certificate as { access?: ResolvedCertificateAccess } | undefined)?.access,
  );

  /**
   * Three states, not two, and the third is why this is not inlined.
   *
   * The gate shows when the brand collects EITHER an email or a location. The
   * content shows when the gate does not apply — but NOT while the settings are
   * still unresolved, because rendering it then would flash the certificate and
   * only afterwards drop the gate over it, which both looks broken and defeats
   * the capture the gate exists for. So in the unresolved window neither renders.
   *
   * That window always ends: `useCertificateAccessSettings` resolves on error as
   * well as on success, so a failing lookup yields a readable certificate with
   * nothing collected rather than a permanently blank page.
   *
   * ## `collectEmail || collectLocation`, and why the OR is the whole fix
   *
   * `showAccessGate` read `collectEmail` ALONE. The two settings are independent
   * and the platform lets a brand configure "Require a location to view" with
   * email off — in which case this predicate was false, so no gate mounted
   * anywhere, no location was ever asked for, and the details simply opened. The
   * platform's label described a control that did not exist.
   *
   * `showGatedContent`'s negation had the same hole from the other side: it
   * released the content the moment `!collectEmail` resolved, which for a
   * location-only brand is immediately. The two are complements — one decides
   * whether to gate, the other whether to release — so widening one without the
   * other would either paint the gate over already-visible content or withhold
   * both forever. They move together, off one shared term.
   */
  /*
   * VRG-781 — the PREVIEW simulates the gate as well.
   *
   * It did not, and `OWNER_PUBLIC_PREVIEW` was a deliberate carve-out (see the
   * `isAccessGateUndecided` note below, which names the three viewers who are never
   * gated). That was defensible while the preview answered "what does my share view
   * reveal" — the gate is the BRAND's, not the owner's. It stops being defensible now
   * that curated custom fields sit behind it (VRG-104): "what a visitor sees" and "what
   * the preview shows" then diverge at exactly the point a brand would check, and the
   * preview is the only place a brand can ever see their own gate, since they are never
   * an anonymous scanner of their own certificate.
   *
   * Deliberately ignores `isAccessDataCollected` — the owner's own stored consent on
   * this origin is not the visitor's, and a preview that skipped the gate because the
   * owner had already answered it would show the one thing it exists to show. The sticky
   * "Public preview" banner is on screen throughout, so nothing here can be mistaken for
   * the owner's own view being gated.
   */
  const publicGateApplies =
    (viewType === ViewTypes.PUBLIC && !isAccessDataCollected) ||
    viewType === ViewTypes.OWNER_PUBLIC_PREVIEW;
  const accessGateRequired =
    accessSettings.collectEmail || accessSettings.collectLocation;
  const showAccessGate = publicGateApplies && accessGateRequired;
  /*
   * VRG-720 slice 2 -- claim on scan. When the product has it on and nobody has claimed the
   * item yet, the PUBLIC view offers "Claim ownership" in place of the access form (the claim
   * flow logs the visitor's access itself). It never shows to the owner, in the Studio
   * preview, or once the visitor chooses to just view the item. `claimable` is resolved by
   * the server and fails closed, so an old API simply never sets it.
   */
  const showClaimCard =
    viewType === ViewTypes.PUBLIC &&
    !isPreview &&
    !claimDismissed &&
    (certificate as ClaimOnScanFields | undefined)?.claimable === true;
  /*
   * VRG-720 comparison harness — `?present=sheet` renders the access gate and the
   * claim prompt as a bottom sheet instead of an in-flow card, so the two can be
   * compared on the same certificate. Default is the card, i.e. today's behaviour,
   * so a normal URL is untouched.
   *
   * A QUERY PARAM rather than a build flag or a second deployment: the whole point
   * is to look at both on the same record, with the same data, seconds apart. Two
   * servers would also differ in whatever else drifted between them.
   *
   * Temporary. Whichever presentation wins, this and `gatePresentation` go.
   */
  /*
   * ⚠️ `window.location`, NOT the router's `useSearchParams()`. Measured, not
   * assumed: with `?…&present=sheet` in the address bar, the router reported
   * `present: null` while `window.location.search` reported `sheet` on the same
   * render. The router's location carries a SANITISED search string here — the
   * certificate entry URL is rewritten after the scan token is exchanged — so a
   * param that is not one the app already knows about never reaches
   * `useSearchParams`. Anything added to this URL for debugging has to read the
   * real location.
   */
  const gatePresentation =
    new URLSearchParams(window.location.search).get('present') === 'sheet'
      ? 'sheet'
      : 'card';

  const showGatedContent =
    !publicGateApplies || (accessSettings.isResolved && !accessGateRequired);

  /**
   * Whether we know yet which of the two things belongs below the title — the details,
   * or the gate with the details blurred behind it. Until we do, hold the page.
   *
   * ## Why this holds the WHOLE page and not just the lower half
   *
   * `showGatedContent`'s negation already held the lower half through this window, and
   * that is nearly enough: the hero and the title are ungated content, so painting them
   * early discloses nothing. What it cannot prevent is the SECOND term below, where the
   * page has every reason to believe it should render the details and then discovers it
   * should not — so it would paint them and retract them. A gate that appears over
   * content the visitor has already read is not a gate.
   *
   * COST: none on the normal path. VRG-591 moved the answer onto the certificate itself
   * (`access`, resolved server-side), so `isResolved` is true the moment the certificate
   * lands rather than one round trip later. The company payload is now only a fallback
   * for a response whose own resolution failed.
   *
   * FIRST TERM — settings not in, and no cached consent to go on.
   *
   * SECOND TERM — the cached-consent re-check (`cachedConsentVerdict`). A returning
   * visitor is seeded `isAccessDataCollected = true` before anything is known, so without
   * this the page would paint the details and only then discover the stored consent no
   * longer covers a now-required location, dropping the gate over what it had already
   * shown. The check behind it is local (`navigator.permissions`, no network) and
   * short-circuits when no location is required, so this is not a new round trip.
   *
   * The three carve-outs are the viewers who are never gated at all — Studio's preview,
   * an authenticated visitor, and the owner's own view — so making any of them wait
   * would be charging them for a decision that does not apply to them.
   */
  const isAccessGateUndecided =
    !isPreview &&
    // VRG-781 — the preview waits for the same answer, for the same reason: with the
    // gate now simulated there, rendering before `isResolved` would paint the details
    // and then drop the gate over what the brand had already read. It is NOT gated on
    // `!profile` like the branch below — in preview the viewer IS authenticated, which
    // is the whole situation.
    ((viewType === ViewTypes.OWNER_PUBLIC_PREVIEW && !accessSettings.isResolved) ||
      (!profile &&
        viewType === ViewTypes.PUBLIC &&
        ((!isAccessDataCollected && !accessSettings.isResolved) ||
          cachedConsentVerdict === 'pending')));

  /**
   * VRG-388 — the disclosure must survive TAKEOVER.
   *
   * Under the acquisition model the prospect is invited into the SAME company the demo was
   * provisioned into, and moves onto their own subdomain. At that instant `isDemoHost()` goes
   * false, and keyed on hostname alone the banner would vanish — leaving scraped imagery and
   * guessed descriptions rendering as ordinary certificates on a real brand's live site. That
   * is the artifact this whole epic exists to prevent, appearing at the one moment nobody is
   * watching for it.
   *
   * So the gate is OR, and the two halves fail in opposite directions ON PURPOSE:
   *   - the hostname half cannot be suppressed by a failed request (fails open), and covers
   *     cold outbound even if the API is down;
   *   - the `example_kind` half needs the API, but can only ever ADD a disclosure, so its
   *     failure mode degrades to exactly today's behaviour rather than to a silent page.
   * Neither can turn the other off. There is deliberately no configuration that can.
   */
  const isExampleCertificate = certificate?.example_kind != null;
  const isDemoCertificate = isDemoHostname || isExampleCertificate;

  // 404 from the API = genuinely no such certificate (bad/unregistered tag).
  // Any OTHER error (network, 500, timeout) = the lookup itself failed — a
  // different message (retry + contact-the-brand) than "this tag is invalid".
  const isCertificateLookupError =
    isCertificateError && (certificateError as any)?.response?.status !== 404;

  const { editTransfer } = useTransferCertificate();
  // VERA-415 / VERA-908: surface an invalid scan at the TOP of the certificate, not only
  // as a chip buried in the Scans tab. An invalid scan is a possible counterfeit, and the
  // owner shouldn't have to go looking for it.
  //
  // Same limit the map uses (500), so react-query's key ['scan-logs', id, params] matches
  // and this shares that cache rather than firing a second request.
  /*
   * The id comes from the ROUTE, not from the resolved certificate — and that one
   * word is the whole fix for "the sections fall in at different durations".
   *
   * Reading `certificate?.id` made this a serial waterfall: the query could not
   * start until the certificate request had already come back, so the page
   * painted looking finished and THEN, a full round-trip later, the invalid-scan
   * banner inserted itself at the top and pushed everything down. Both endpoints
   * key on the same id (`certificates/public/<id>` and `scan-log/public/<id>`),
   * so there was never anything to wait for.
   *
   * `certificate?.id` stays as the fallback for the PREVIEW route, which has no
   * path param and gets its id from PreviewContext.
   */
  const { data: certificateScanLogs, isLoading: isLoadingScanLogs } = useScanLogs({
    certificateId: certificateId || certificate?.id,
    limit: 500,
  });
  // The most recent invalid scan, so the banner can say WHERE it happened. "An invalid
  // scan was recorded." on its own leaves the reader with nowhere to start; a place
  // makes it immediately actionable ("that isn't anywhere I've been").
  const latestInvalidScan = useMemo(
    () => (certificateScanLogs?.data ?? []).find(scan => isInvalidScan(scan as any)),
    [certificateScanLogs?.data],
  );
  const hasInvalidScan = Boolean(latestInvalidScan);
  /** For the Scans row's standing badge — the banner is dismissible, this is not. */
  const invalidScanCount = useMemo(
    () => (certificateScanLogs?.data ?? []).filter(scan => isInvalidScan(scan as any)).length,
    [certificateScanLogs?.data],
  );
  const invalidScanPlace = latestInvalidScan
    ? getScanLocationLabel((latestInvalidScan as any).metadata)
    : null;
  // Dismissible per visit, not persisted: on the next open the item is still flagged, and
  // silencing a possible-counterfeit warning permanently from one tap is the wrong default.
  const [invalidScanNoticeDismissed, setInvalidScanNoticeDismissed] = useState(false);

  const isCertificateOwner = isCertificateOwnedBy(certificate, profile?.id);

  /**
   * VERA-315 — desktop is the two-column layout above; see the constants at the top
   * of this file. Two things need the boolean in JS rather than in a media query:
   * the header's `solid` state (below) and the `DrawerPopup` presentation.
   */
  const muiTheme = useTheme();
  const isDesktopLayout = useMediaQuery(muiTheme.breakpoints.up('md'));

  /**
   * VERA-315 Phase 7 — the header goes solid once the hero has scrolled under it.
   *
   * Measured off the hero ELEMENT rather than a scroll threshold: the hero is a
   * photograph whose height varies by product, and it collapses to a placeholder
   * when there is none, so any constant would be wrong for most certificates.
   */
  const heroRef = React.useRef<HTMLDivElement | null>(null);
  // VRG-749 — a model counts as hero media; see `mediaColSx` for why this matters.
  const hasHeroMedia = Boolean(
    certificate?.product?.model_url || certificate?.product?.images?.length,
  );
  const headerSolid = useScrolledPast(heroRef, NAV_HEADER_HEIGHT);
  // VERA-302: the Share screen's Preview action deep-links here with ?previewView=<slug>,
  // so the owner previews THAT view (public today, custom links from VERA-300).
  const previewViewSlug = searchParams.get('previewView');

  // VERA-299: the certificate's shareable views. Owner-only endpoint, so it is only
  // fired for the owner; a public scanner never requests it.
  /*
   * Warm the Activity screen's first page while the user is still reading this one.
   *
   * The three sub-view rows push to their own screens, and a screen that starts
   * fetching only once it has mounted renders its header, then drops its content
   * in a beat later — which is exactly what the push animation puts under a
   * magnifying glass, since the slide is carrying a half-built page.
   *
   * Scans needs no prefetch: this page already fetches that certificate's scan
   * logs at a wider window, and useScanLogs now serves the narrower one out of it
   * (see sliceFromWiderWindow). Provenance derives from the certificate itself.
   * Activity is the only sub-view with a request nothing else has made, so it is
   * the only one warmed here.
   *
   * Owner-only, matching the rows: a non-owner cannot reach these screens, so
   * fetching for them would be a request nobody will ever use.
   *
   * `prefetchQuery` does nothing if the key is already cached and fresh, so
   * returning to this page repeatedly does not re-request.
   */
  useEffect(() => {
    if (!certificateId || !isCertificateOwner) return;
    queryClient.prefetchQuery(
      certificateActivityQuery({ certificateId, limit: ACTIVITY_PAGE_SIZE }),
    );
  }, [certificateId, isCertificateOwner, queryClient]);

  const { data: certificateViews } = useCertificateViews(certificateId, isCertificateOwner);
  const publicVisibility =
    certificateViews?.find(view => view.kind === 'public')?.visibility ?? null;

  // VERA-302: what the PREVIEWED view exposes. Falls back to the public view when no
  // slug was supplied. A disabled view exposes nothing, mirroring the server.
  const previewedView = previewViewSlug
    ? certificateViews?.find(view => view.slug === previewViewSlug)
    : certificateViews?.find(view => view.kind === 'public');
  const previewVisibility = previewedView?.is_enabled
    ? previewedView.visibility
    : previewedView
      ? {show_ownership: false, show_provenance: false, show_prices: false, show_activity: false}
      : null;

  // What the PUBLIC link exposes, straight from the view — the same record the server
  // enforces. The legacy `certificate.settings` blob is no longer consulted.
  const isPublicProvenanceVisible = Boolean(publicVisibility?.show_provenance);
  const isPublicActivityVisible = Boolean(publicVisibility?.show_activity);

  // What THIS viewer may see. The owner always can — except while PREVIEWING, where
  // the whole point is to show what a visitor gets, so the previewed view's rules win.
  const isPreviewingPublicly = viewType === ViewTypes.OWNER_PUBLIC_PREVIEW;
  const isShowProvenance = isPreviewingPublicly
    ? Boolean(previewVisibility?.show_provenance)
    : isCertificateOwner || isPublicProvenanceVisible;
  const isShowScanHistory = isPreviewingPublicly
    ? Boolean(previewVisibility?.show_activity)
    : isCertificateOwner || isPublicActivityVisible;
  // VERA-299: the current-owner block appears on BOTH the Product surface and inside
  // Provenance. One toggle has to govern both, or the provenance surface leaks the
  // identity that "Show your ownership" claims to hide.
  const isShowOwnershipBlock = isPreviewingPublicly
    ? Boolean(previewVisibility?.show_ownership)
    : isCertificateOwner || Boolean(publicVisibility?.show_ownership);
  const ownerData = certificate && getOwner(
    certificate?.customer,
    certificate?.company,
    certificate?.transfer?.type,
    // VERA-304: while a transfer is still pending the payload carries no `customer`,
    // so the sender of that transfer is who currently owns it. See `getOwner`.
    certificate?.transfer?.fromCustomer
  );
  const isShowAcceptTransfer =
    openAcceptTransfer && certificate?.status !== 'active' && certificate?.status !== 'inactive';

  const isOwnerView = useMemo(() => {
    return viewType === ViewTypes.OWNER;
  }, [viewType]);

  /*
   * VRG-511 — is this viewer someone who USED to own the item?
   *
   * The consent endpoint answers it: it 404s (mapped to a resolved `null`) when the
   * caller has no settled hop on this certificate, and 200s with their stored
   * preference when they do. So one request establishes both "you were here" and
   * "this is your current answer", which is what the card needs.
   *
   * NOT derived from the provenance list, and the reason is the feature itself: a
   * past owner reads that list through the PUBLIC endpoint, where their own hop is
   * anonymised the moment they opt out. They would vanish from their own evidence
   * and the card would disappear with them — the control removing the only route
   * back to itself.
   *
   * Gated on `isAuthenticated && !isOwnerView`: the current owner has no use for it
   * (their identity is governed by `show_ownership`, and the API routes their hop
   * there), and an anonymous scanner has no session to ask about, so firing it for
   * either is a request that can only 404.
   */
  const { data: provenanceConsent, isLoading: isConsentLoading } =
    useProvenanceConsent(
      certificateId || undefined,
      Boolean(isAuthenticated) && !isOwnerView,
    );
  const isPastOwnerView = Boolean(provenanceConsent) && !isOwnerView;
  const updateProvenanceConsent = useUpdateProvenanceConsent(certificateId || '');

  /*
   * VRG-720 slice 3 -- can the server release this item's contact card to this viewer?
   *
   * The card is never in the public payload, so this decides only whether to ASK. The server
   * is the authority (it answers 403 to anyone without access, which resolves to "no card"),
   * this just avoids requests that can only fail:
   *   - an owner, current or previous: always (they may hold it on any product);
   *   - an ungated product: anyone, EXCEPT a claim-on-scan product, which releases it to
   *     owners only, so a visitor who merely tapped the card is not asked;
   *   - an access-GATED product: never asked for here. Its card arrives in the response to
   *     the gate's own access POST (RequestAccessInfo, and the returning-visitor effect above)
   *     and is read from the same cache entry.
   * Never in Studio's preview or the owner's "preview as public" view, which show a visitor's
   * page rather than a real viewer's access.
   */
  const canRequestContactCard =
    !isPreview &&
    viewType !== ViewTypes.OWNER_PUBLIC_PREVIEW &&
    Boolean(certificate) &&
    accessSettings.isResolved &&
    (isOwnerView ||
      isPastOwnerView ||
      (viewType === ViewTypes.PUBLIC &&
        !accessGateRequired &&
        !(certificate as ClaimOnScanFields | undefined)?.claim_on_scan));

  /**
   * VRG-782 — does this SKU have Experience copy worth a heading?
   *
   * Asked of the SANITIZED html, not of the raw string, because the rich-text editor
   * stores `<p></p>` for a field someone emptied — seven characters that are truthy,
   * contain no text, and would otherwise put an "Experience" heading over nothing.
   * `customFieldHtmlHasText` is the same test the brand custom-field slot uses, and the
   * registry's `brandSections` uses it for the owner's row, so the row and the public
   * heading cannot disagree about whether this SKU has content.
   */
  const experienceHtmlPresent = useMemo(
    // `EXPERIENCE_SECTION_ENABLED` first, and it is the WHOLE reason this reads a flag
    // from the registry rather than answering the content question alone: the owner's
    // row comes from `brandSections` and this public block does not, so the section has
    // two independent gates. Gating only the registry would have left a brand's copy
    // hidden from the owner who authored it and still published to every stranger who
    // scanned the tag — the one combination that is worse than either state. See the
    // flag's docblock (VRG-787).
    () =>
      EXPERIENCE_SECTION_ENABLED &&
      customFieldHtmlHasText(certificate?.product?.experience_content),
    [certificate?.product?.experience_content],
  );

  /**
   * Is this certificate reported missing, to its owner?
   *
   * ONE flag gates the card AND the action inside it, which is possible again
   * because the two coincide: the card shows for lost and stolen, and the action
   * lives in the card. VERA-301 also had one flag; Phase 1 (action out to a
   * separate button) and Phase 5 (card stolen-only) pulled them apart, and a
   * second flag `showsMissingStateCallout` existed to carry the difference. Both
   * of those changes have since been reverted, so that flag was an exact alias of
   * this one and was removed rather than left to drift.
   *
   * NOT used for the card's `status` prop — that reads `lastMissingStatus`, which
   * outlives the flip to 'active' so the card can animate away showing the state
   * it is closing rather than going blank mid-collapse.
   */
  const isMissing =
    isOwnerView && (certificate?.status === 'stolen' || certificate?.status === 'lost');

  // VERA-304: the same single-source treatment for the pending-transfer card — the
  // transfer-flow statuses are carried by THAT card's heading row, never also by a
  // chip beside the product title, which is how a pending transfer used to print its
  // status twice. (The title-row chip this once guarded is gone entirely as of
  // VRG-720; `showsTransferCard` survives because the state card still needs it.)
  // The hook is shared with TransferSummarySection so
  // the guard and the card can't disagree.
  const { visible: showsTransferCard, transferType } = useTransferCardState(
    certificate || null,
  );

  /**
   * VERA-304 — is THIS viewer the person a live transfer is coming TO?
   *
   * `isOwnerView` cannot answer that, and the reason is structural rather than a
   * gap in the check: ownership moves at ACCEPT (`accept-by-code-transfer-by-
   * customer.use-case.ts` sets `certificate.customer_id` to the recipient and
   * only then flips the transfer to `Success`), so for the whole pending window
   * `certificate.customer` is still the SENDER. The recipient is therefore
   * `viewType === PUBLIC` on a certificate that is sitting in their own list.
   *
   * `showsTransferCard` already requires a live transfer (not canceled, not
   * settled) AND that this profile is one of its two parties, so the only thing
   * left to say is the DIRECTION. `gift-out` is excluded because that viewer is
   * the sender, who still owns the record and is already `isOwnerView`.
   */
  const isIncomingTransferView =
    showsTransferCard && (transferType === 'transfer-in' || transferType === 'gift-in');

  /*
   * The brand's mark at the top of a PUBLIC certificate — see
   * `CertificateBrandHeader` for why it is the BRAND's and not Vera's, and why
   * the stranger who just tapped a tag is the person it is for.
   *
   * Gated on the two views that render `NavigationHeader`, because that bar owns
   * this slot when it is present: stacking two 72px bars on a 375-wide phone
   * spends a fifth of the viewport on chrome before the photograph begins, and
   * the owner already knows whose certificate they are holding.
   */
  const showBrandHeader =
    !isOwnerView &&
    !isIncomingTransferView &&
    /*
     * THE SURFACE DECIDES, AND THERE IS DELIBERATELY NO BRAND SETTING.
     *
     * Hosted (brand.app.vera.inc) the bar SHOWS: a stranger who has just tapped a tag
     * needs to know who made the item before the page asks them for an email address.
     * Embedded on the brand's own site it NEVER shows: their site header already carries
     * their logo, so a second mark is duplicate chrome. `hasCertificateBrandHeader`
     * decides that from the embed snippet's own `data-embedded-key`, so the answer is
     * read off the surface rather than stored.
     *
     * A Studio toggle for this was built and then REMOVED (2026-10-30): both values are
     * determined by where the page is rendering, so the control could only ever be set
     * to what the surface already implies, or to the wrong thing. Its only reachable
     * novel state was "hosted, but unbranded" — an unidentified page asking a stranger
     * for their email, which is the failure the bar exists to prevent. A column, a
     * migration, a Studio card and a permission decision bought that and nothing else.
     * If this is ever reopened, the question to answer first is which brand genuinely
     * wants their own hosted certificate page to omit their own mark.
     */
    hasCertificateBrandHeader(
      certificate?.company,
      // In the preview there is no snippet to read, so the install location the brand
      // picked in Studio stands in for one. Everywhere else `undefined` asks the page.
      isPreview ? (previewEmbedded ? 'studio-preview-embed' : null) : undefined,
    );

  /*
   * The missing-state CARD shows for BOTH statuses again (2026-08-22), which is
   * why there is no longer a separate flag here — see `isMissing` above.
   *
   * Phase 5 made the card stolen-only, and its reasoning was sound GIVEN Phase 1:
   * with `Report found` moved out, the lost card was "a bordered box containing a
   * four-word heading and a chip — chrome around two lines". That premise is gone
   * now the action is back inside, so the lost card is a heading, a status chip
   * and the button that ends the state.
   *
   * If you are tempted to make it stolen-only again, check FIRST whether the
   * action still lives in the card; that is the fact the argument turns on.
   */

  // VERA-301: the LAST real lost/stolen status, held past the moment the
  // certificate flips back to 'active'. Needed because the missing-state card is
  // wrapped in a Collapse that animates OUT rather than disappearing instantly —
  // during that animation `certificate.status` is already 'active' (not a
  // valid MissingStatus), so without this the card would show blank/wrong
  // content while still visible mid-collapse instead of "Stolen" shrinking away.
  //
  // VERA-315: that Collapse now belongs to `StateCardStack` (it wraps every item
  // uniformly), and this value additionally feeds the collapsed summary row's
  // label — same reason, so the row does not go blank mid-collapse either.
  const [lastMissingStatus, setLastMissingStatus] = useState<'lost' | 'stolen' | null>(null);
  useEffect(() => {
    if (certificate?.status === 'lost' || certificate?.status === 'stolen') {
      setLastMissingStatus(certificate.status);
    }
  }, [certificate?.status]);

  const handleAcceptTransfer = () => {
    openHowToScan && setOpenHowToScan(false);
    setOpenAcceptTransfer(true);
  };

  const closeSuccessPopup = () => {
    editTransfer({
      transferId: certificate?.transfer?.id as string,
      metadata: { isSuccessPopupClosed: true },
    });
    setIsShowSuccessOwnership(false);
  };

  const handleOpenVideoGuide = () => {
    setOpenHowToScan(false);
    setOpenVideoGuide(true);
  };

  /**
   * Back slides the certificate off to the RIGHT, then navigates.
   *
   * The delay is not decoration: `navigate()` unmounts this page immediately, so
   * animating first and routing after is the only way the exit is visible at
   * all. `exiting` is a ref as well as state — a second tap during the animation
   * would otherwise queue a second navigate.
   */
  const [exiting, setExiting] = useState(false);
  const exitingRef = React.useRef(false);
  const handleBack = () => {
    if (exitingRef.current) return;
    if (!shouldSlide()) {
      navigate('/certificates');
      return;
    }
    exitingRef.current = true;
    setExiting(true);
    window.setTimeout(() => navigate('/certificates'), PAGE_SLIDE_MS);
  };

  /**
   * VERA-298: one share entry point — the Share screen, which lists every view AND
   * manages them. The drawer this used to open only ever offered the native share
   * sheet, which is now a per-row action on that same screen.
   */
  const handleShare = () => navigate(`/certificates/${certificateId}/share`);

  /**
   * VRG-778 — share from a PUBLIC certificate page.
   *
   * Deliberately NOT `handleShare`. That one opens the owner's Share SCREEN, which
   * lists and manages their access links — a management surface, and meaningless to
   * someone who does not own this certificate. Spencer: "share on public is just the
   * native open how to share." So this is the OS sheet and nothing else.
   *
   * The token is minted here, synchronously, and appended as `?s=` before the sheet
   * opens. Two reasons it cannot be fetched from the server first: it would cost the
   * transient user activation the Web Share API requires (see `shareLink`), and the
   * link has to CARRY the token to be correlatable at all. The recording POST goes
   * out in parallel from `onShared`, after the sheet is already up.
   *
   * `actor_kind` is not sent — the server derives it from the auth token plus an
   * ownership check, because distinguishing the owner's own distribution from the
   * distribution they got for free is the entire point of the field.
   */
  const recordShare = useRecordCertificateShare();
  const recordShareVisit = useRecordCertificateShareVisit();

  /**
   * VRG-778 — the other half of a share: count the OPENS it produced.
   *
   * Without this, a share figure means "sheets opened", because `navigator.share()`
   * resolves on DISMISSAL and never reports whether anything was sent. The `?s=`
   * token is what makes the two correlatable, so a load presenting one is recorded
   * against that share.
   *
   * The token is deliberately left IN the address bar rather than stripped the way
   * `?theme=` is: this link gets forwarded on, and removing the token would break
   * the chain at the first re-share. The hook is idempotent per
   * (certificate, token) within a session, so a reload is not a second open.
   */
  useEffect(() => {
    if (!certificateId) return;
    const token = new URLSearchParams(window.location.search).get('s');
    if (token) recordShareVisit(certificateId, token);
  }, [certificateId, recordShareVisit]);
  const { openSnackbar } = useSnackbar();

  const handlePublicShare = () => {
    if (!certificateId) return;
    const shareToken = mintShareToken();
    const url = new URL(window.location.href);
    url.searchParams.set('s', shareToken);

    void shareLink({
      url: url.toString(),
      title: certificate?.product?.title || 'Vera certificate',
      label: 'Certificate link',
      onCopied: (message: string) =>
        openSnackbar({ severity: 'success', title: message }),
      onShared: method => recordShare({ certificateId, shareToken, method }),
      // A dismissed sheet is not a failure and never reaches here; this is the
      // genuinely-nowhere-to-put-it case (insecure origin, clipboard denied).
      onError: () =>
        openSnackbar({ severity: 'error', title: 'Could not share this link' }),
    });
  };


  const handleConfirmStatusChange = () => {
    if (!certificate?.id) return;

    dispatch(
      changeCertificateStatusByCustomer(
        { certificateId: certificate.id, status: 'active' },
        (res: any) => {
          if (res?.success) {
            setOpenConfirmStatusChange(false);
            // VERA-301: was `window.location.reload()`. apps/app runs on a
            // MemoryRouter (no URL bar to restore from), so a hard reload
            // drops all in-memory route state and lands back on the
            // certificates LIST, not this certificate — the user reported
            // exactly this after clicking "Report found". Refetching the one
            // query this page reads (same key already used at the
            // certificateId/isAuthenticated effect below) updates
            // `certificate.status` to 'active' in place, keeps the user on
            // this page, and lets the missing-state card animate out instead of
            // the whole page flashing. The Collapse that does that moved into
            // `StateCardStack` in VERA-315; it used to be written inline here.
            queryClient.invalidateQueries({
              queryKey: ['certificate-public', certificateId],
            });
          }
        }
      )
    );
  };

  useEffect(() => {
    setOpenTransferOwnership(false);
  }, [certificate?.status, isExpired]);

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['certificate-public', certificateId] });
  }, [certificateId, isAuthenticated, openTransferOwnership]);

  /*
   * VRG-591 — RE-CHECK the stored consent against the requirement as it stands NOW, and
   * re-gate when it no longer covers it.
   *
   * `isAccessDataCollected` is seeded synchronously from "a consent exists", because it
   * must be decided before first paint and nothing else is known that early. That seed is
   * an assumption, and `collect_location` becoming a hard gate is what invalidates it: a
   * consent written when location was optional asserts nothing about location, and one
   * written under a browser grant the visitor has since revoked asserts something no
   * longer true. Either would be a 180-day bypass, and the second is also the shape of a
   * deliberate one — satisfy the gate once, revoke the permission, read for six months
   * with nothing ever recorded.
   *
   * Setting `isAccessDataCollected(false)` is what re-opens `showAccessGate`, so the card
   * comes back — with the stored email pre-filled, since that half is not what failed.
   *
   * ONE PREDICATE, `cachedAccessSatisfies`, called with ONE argument shape. It used to be
   * called from here AND from the gate's own route, and they had to agree exactly or a
   * visitor would be bounced between the two forever. The route is gone, so that risk is
   * gone with it — but the requirement stays an object rather than two positional
   * booleans, because a caller that transposes `email` and `location` is the failure this
   * shape rules out and a second caller may yet appear.
   *
   * Skipped entirely for preview, for an authenticated profile, and for the owner view:
   * none of them are gated, so re-gating them would be inventing a gate rather than
   * enforcing one.
   */
  useEffect(() => {
    if (cachedConsentVerdict !== 'pending') return;
    if (isPreview || profile || viewType !== ViewTypes.PUBLIC) {
      setCachedConsentVerdict('ok');
      return;
    }
    // The requirement is an input to the answer. Asking before it resolves would read
    // BOTH flags as their unresolved `false` and confirm a consent that covers neither.
    if (!accessSettings.isResolved) return;

    let cancelled = false;
    // THE SAME CALL, WITH THE SAME ARGUMENT, as the gate route makes. If the two could
    // disagree a visitor would be thrown between them forever — which is why the
    // requirement is one object rather than two positional booleans one site could
    // transpose.
    cachedAccessSatisfies({
      email: accessSettings.collectEmail,
      location: accessSettings.collectLocation,
    }).then((consent) => {
      if (cancelled) return;
      if (consent) {
        setCachedConsentVerdict('ok');
        return;
      }
      setCachedConsentVerdict('insufficient');
      setIsAccessDataCollected(false);
    });
    return () => {
      cancelled = true;
    };
  }, [
    cachedConsentVerdict,
    isPreview,
    profile,
    viewType,
    accessSettings.isResolved,
    accessSettings.collectEmail,
    accessSettings.collectLocation,
  ]);

  // Log the access even when the gate was SKIPPED because consent was already stored.
  //
  // Without this, remembering the consent would silently delete the brand's access
  // analytics for every repeat visit — trading a UX annoyance for data loss, which is a
  // worse bug than the one being fixed. The server log is the authoritative per-access
  // record; the stored consent is only a convenience cache.
  //
  // The POSITION is re-acquired rather than replayed from storage: a months-old position
  // logged against a new scan is corrupt data, not convenience. Re-acquisition is silent
  // and gated on the browser permission already being `granted`, so this never raises a
  // prompt the visitor did not initiate.
  //
  // THE STORED CONSENT DOES NOT OUTRANK THE BRAND'S SETTING. A visitor may hold a
  // 180-day cached consent from before the brand switched collection off — or from
  // another certificate on the same origin — so this path is gated on the live
  // settings exactly as the gate itself is. Without that, turning `collect_email`
  // off would stop new visitors being asked while quietly continuing to log every
  // returning one, which is the harder half of the defect to notice.
  useEffect(() => {
    if (isPreview || !certificateId) return;
    if (viewType !== ViewTypes.PUBLIC) return;
    // Unresolved is NOT permission. Wait, then obey.
    //
    // `accessGateRequired`, not `collectEmail`: a location-only brand collects — it just
    // collects the other half — and gating this on the email flag alone would stop
    // logging every repeat scan for that configuration while the gate itself worked.
    // That is the quieter half of the same defect, and the harder one to notice.
    if (!accessSettings.isResolved || !accessGateRequired) return;
    /*
     * ⚠️ ONE ROW PER VISIT, and what guarantees it is `isAccessDataCollected` being
     * ABSENT from this effect's dep array.
     *
     * A FRESH grant is POSTed by `RequestAccessInfo` itself. Its submit then writes the
     * consent cache and flips `isAccessDataCollected`, so an effect that re-ran on that
     * flip would find the consent this very submit just cached and log the same visit a
     * second time. It does not re-run, because nothing it depends on changed and the
     * gate is an inline card — the card unmounts, this page does not.
     *
     * While the gate was briefly a separate ROUTE (5f6eeca50) the submit WAS followed by
     * a genuine remount of this page, which no dep array can suppress, so that revision
     * carried an `accessLogged` flag in router state to stand this effect down. Both the
     * route and the flag are gone. Do not reintroduce either half without the other.
     */

    /*
     * VRG-591 — and the stored consent must still COVER the requirement.
     *
     * `ok` is the only value that may log. `pending` would log before the re-check
     * above has run, i.e. before we know whether this visitor is entitled to be here;
     * `insufficient` would log an access for someone the gate is, in the same tick,
     * closing over — writing the brand a row asserting a location consent that has just
     * been found not to hold.
     */
    if (cachedConsentVerdict !== 'ok') return;

    const consent = readAccessConsent();
    // Only the cached path — a fresh grant is logged by RequestAccessInfo's own POST.
    if (!consent) return;

    let cancelled = false;
    (async () => {
      const location =
        consent.locationConsent && accessSettings.collectLocation
          ? await getLocationIfAlreadyPermitted()
          : null;
      if (cancelled) return;
      try {
        const accessResponse = await createApiCall({
          method: 'POST',
          url: `certificates/public/${certificateId}/access`,
          data: {
            // Sent only when the brand collects one AND the cache holds one. The
            // endpoint refuses an email submitted under `collect_email: false` — a
            // 180-day-old consent from before the brand switched email off would
            // otherwise 403 this visitor's every repeat scan.
            ...(accessSettings.collectEmail && consent.email
              ? { email: consent.email }
              : {}),
            consent: consent.locationConsent && accessSettings.collectLocation,
            location: location ?? undefined,
          },
        });
        // VRG-720: a returning visitor to a gated product gets the contact card from this
        // same request, exactly as a fresh grant does from the gate's own POST.
        storeContactCard(queryClient, certificateId, accessResponse);
      } catch {
        // Best-effort, exactly as the gate's own POST is. A failed analytics write must
        // never block someone from reading a certificate they are entitled to see.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    certificateId,
    viewType,
    isPreview,
    accessSettings.isResolved,
    accessSettings.collectEmail,
    accessSettings.collectLocation,
    // NOT `isAccessDataCollected` — see the one-row-per-visit note above. Adding it
    // double-logs every fresh grant.
    cachedConsentVerdict,
  ]);

  useEffect(() => {
    if (isAuthenticated && shopify_user) {
      dispatch(accountLinkCheck({ email: shopify_user }, () => {}));
    }
  }, [isAuthenticated, shopify_user]);

  useEffect(() => {
    setLinkAccounts(linkedAccounts);
  }, [linkedAccounts]);

  // VRG-164: when the OWNER lands on their cert via a FRESH TAP (vt present in the URL),
  // attribute that scan to them so the platform Scans tab shows an "Owner" badge instead
  // of "Anonymous". Gated on the fresh-tap signal so the latest scan is the owner's own
  // tap (not a stranger's earlier scan). Non-fatal; owner-scoped on the server.
  useEffect(() => {
    const freshTap = searchParams.get('vt');
    if (viewType === ViewTypes.OWNER && freshTap && certificateId) {
      createApiCall({
        method: 'POST',
        url: `certificates/customer/${certificateId}/attribute-owner-scan`,
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewType, certificateId]);

  useEffect(() => {
    if (viewType === ViewTypes.OWNER_PUBLIC_PREVIEW) return;

    // VERA-302: deep-linked from the Share screen's Preview action.
    if (previewViewSlug && isCertificateOwner) {
      setViewType(ViewTypes.OWNER_PUBLIC_PREVIEW);
      return;
    }

    if (isPreview) {
      /*
        Studio previews TWO different screens, and which one is a property of the
        dropdown, not of the signed-in user. 'Certificate' is the owner's view;
        'Certificate (public view)' is what a stranger who taps the tag sees.
        Resolving the viewer here means every downstream gate — the nav bar, the brand
        header, the owner actions — keeps its PRODUCTION condition and still composes
        correctly in both previews. The alternative, special-casing `isPreview` at each
        gate, is how a preview drifts from the page it claims to preview.
      */
      setViewType(previewPublicView ? ViewTypes.PUBLIC : ViewTypes.OWNER);
      return;
    }

    if (!certificate || !isAuthenticated) {
      setViewType(ViewTypes.PUBLIC);
      return;
    }

    // VERA-299: `customer` is now null when the public view hides ownership, so an
    // authenticated non-owner gets no owner object at all. Guard on the id being
    // present BEFORE comparing — bare optional chaining would make `undefined ===
    // undefined` true for a signed-out-ish profile and wrongly grant OWNER.
    const certificateOwnerId = certificate.customer?.id;

    if (
      certificateOwnerId &&
      (certificateOwnerId === profile?.id ||
        certificateOwnerId === profile?.userId)
    ) {
      setViewType(ViewTypes.OWNER);
      /*
       * VRG-781 — ask which view, exactly once per arrival.
       *
       * Fired HERE rather than in an effect of its own, because this branch is already
       * the one place that has decided "this viewer is the owner of THIS certificate" —
       * the predicate the sheet must never fire without. A separate effect would have to
       * re-derive it, and the obvious re-derivation (a session exists) is the bug this
       * branch's own VERA-299 note exists to prevent: it would ask a signed-in stranger
       * which of two views to open, one of which is not theirs to see.
       *
       * Owner view is set FIRST and the sheet opens over it, so dismissal needs no
       * special case — it is already the state underneath. The cost is that the owner's
       * own details are on screen behind the backdrop for the moment before they
       * choose; the alternative is a blank interstitial between the tap and the record,
       * which is worse for every arrival that just wanted to look at their own item.
       */
      if (!viewChoiceAsked) {
        setViewChoiceAsked(true);
        setOpenViewChoice(true);
      }
      return;
    }
    // `previewPublicView` is read by the `isPreview` branch above (main); the disable
    // covers `viewChoiceAsked` and `profile`, which this effect reads and omits on
    // purpose — listing `viewChoiceAsked` would re-run on the flip that the VRG-781
    // block above just set, and re-open the sheet it exists to show exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certificate, isAuthenticated, isPreview, previewPublicView]);

  /**
   * VERA-304 (A1) — the possession-verification mutation.
   *
   * It sits HERE, above the early returns below, and not next to
   * `canVerifyPossession` / `submitPossessionScan` where it reads more naturally.
   * That is not a style preference, it is the rules of hooks: declared after
   * `if (showPageSkeleton) return` / `if (isPageLoading) return`, its call is
   * conditional on the page having finished loading, so the FIRST render of a
   * cold load (155 hooks -> 154) and the render after the data lands (154 -> 155)
   * disagree on the hook count and React throws "Rendered more hooks than during
   * the previous render", taking the whole page to the error boundary.
   *
   * It shipped that way and it was not hypothetical: every cold entry through
   * `?certificate_id=` crashed — the NFC scan redirect AND the "Accept transfer"
   * link in the transfer email, i.e. the one route a recipient reaches from their
   * inbox. A warm navigation from the certificates list did NOT crash, because
   * `placeholderData` makes `isPageLoading` false on the very first render, so
   * neither early return ever fires and the count never changes. That asymmetry
   * is why it survived local clicking-around.
   *
   * The comment on `useDeferredLoading` below asserts "nothing above this point
   * returns from the component". Its converse is the load-bearing half and is
   * stated here: nothing BELOW those returns may call a hook. Add new hooks above
   * this line.
   */
  const { mutateAsync: verifyPossession } = useVerifyPossession(certificateId as string);

  // Gate on the fetch ONLY. The second clause here used to be
  // `(showLoader && !isAuthenticated)`, where `showLoader` cleared on LogoLoader's
  // internal 3.8s timer -- so every anonymous viewer (i.e. every scan) waited out
  // the full animation even when the certificate had already arrived. The wait was
  // the dominant cost in the whole scan journey.
  //
  // A SKELETON, not the branded loader, per §19: by this point the app is mounted
  // and knows the page's shape, so it should show that shape rather than a spinner.
  // The scan loader's job is the gap BEFORE this bundle exists; this is the hand-off.
  /*
   * The COLD path renders whole; the warm path still paints instantly.
   *
   * ## Why the gate now holds for the scan logs
   *
   * The scan logs decide whether the invalid-scan banner exists, and that banner
   * sits at the TOP — so it cannot arrive late without shoving the whole page
   * down. Painting the certificate the moment IT landed and letting the banner
   * follow is what made the page assemble itself in visible stages.
   *
   * It is only affordable because the waterfall above is gone. The two requests
   * now fly together, so the gate costs max(a, b) instead of a + b, and both hit
   * the same API. Adding this while they were still chained would have roughly
   * doubled time-to-first-paint on a page reached by tapping a tag with a phone,
   * which is not a trade worth making.
   *
   * ## Why `isCertificatePlaceholder` and not `!certificate`
   *
   * Arriving from the list, `placeholderData` hands this page the row's data and
   * it paints with ZERO wait (see useGetCertificatePublic). Holding that path for
   * the scan logs would replace an instant page with a few hundred ms of
   * skeleton — and worse, the list->detail slide would animate a skeleton in and
   * swap it for content mid-flight. So the wholeness gate applies exactly where
   * there is nothing to show yet: the cold NFC-tap / shared-link load, where a
   * skeleton is on screen regardless and holding for a parallel request costs
   * almost nothing.
   *
   * `!certificate` cannot express that: placeholder data makes it false, but so
   * does the real response, so the gate would release the moment the certificate
   * arrived and the banner would drop in late again on the cold path too.
   *
   * A DISABLED query reports `isLoading: false` (pending, but not fetching), so
   * the preview route — no id on the first render — is never held here.
   */
  const hasPlaceholderToShow = Boolean(certificate) && isCertificatePlaceholder;
  const isPageLoading =
    (!hasPlaceholderToShow && (isLoadingCertificate || isLoadingScanLogs)) ||
    // Not "still fetching" but "we do not yet know whether the details go below the
    // title or the gate does" — same treatment, because the alternative is painting
    // details we may be about to close a gate over. See `isAccessGateUndecided`.
    isAccessGateUndecided;

  /*
   * Measured at 1280 before this, arriving from the certificates list: 23 skeleton
   * bars mounted and were gone 60.0ms later (119.2ms on a colder run). The skeleton
   * is footprint-exact, so nothing moved — the flicker WAS the transition, which is
   * why the page change read as having none.
   *
   * Safe to call here despite sitting below ~800 lines of hooks: nothing above this
   * point returns from the component (the earlier `return`s are all inside effects and
   * callbacks), so this runs on every render, which is what the rules of hooks require.
   */
  const showPageSkeleton = useDeferredLoading(isPageLoading);

  if (showPageSkeleton) {
    // The reveal rides over the skeleton: on a cold NFC tap this is exactly the load the
    // reveal exists to cover, so this is its normal case rather than an edge one.
    return (
      <>
        {revealOverlay}
        <CertificatePageSkeleton />
      </>
    );
  }

  /*
   * Loading, but inside the skeleton's grace period. Nothing, rather than falling
   * through: everything below dereferences `certificate`, so the loaded branch is not
   * a lighter version of this page, it is a crash.
   */
  if (isPageLoading) {
    // Still the reveal's window, so it keeps the screen rather than flashing away and
    // back as this branch is taken and released.
    return revealOverlay;
  }

  /* THERE IS NO REDIRECT HERE, and its absence is deliberate. A `<Navigate>` to
     `/certificates/:id/access` stood at this exact point and was reverted with the
     route (see `App.tsx`). The gate renders in place, further down, over blurred
     placeholders. */

  /**
   * MANAGE — the discretionary actions, behind the header's kebab.
   *
   * These are things you may CHOOSE to do, as opposed to the state blocks at the
   * top, which are things needing resolution. That split is what replaced the
   * pinned bar: a bar can hold exactly one action and has to rank them, and
   * "which of Gift / Sell / Transfer is the primary" has no stable answer.
   *
   * ## Why an overflow, when this was an in-page list one revision ago
   *
   * The set WILL grow — Transfer and Sell are expected — and neither of the
   * arrangements this has already been through survives that. A pinned bar holds
   * one action; a labelled in-page list scales, but it spends permanent page
   * height on a group whose contents are, by construction, the ones you rarely
   * take, and at md+ it competed with the HISTORY rows for the same idiom (icon,
   * label, chevron) while one navigates and one mutates.
   *
   * The load-bearing point is that the CONTEXTUALLY CORRECT primary is already
   * promoted, and not from here: the state cards carry it. Lost shows `Report
   * found`, a gift out shows `Cancel gift`, a gift in shows `Accept` — each in the
   * card that explains why it is offered. So the overflow is the LONG TAIL, never
   * the primary, and an action being one tap deeper costs the rare case only.
   *
   * ACCEPTED COST, on the user's call: an active certificate has no state card, so
   * the kebab is the only route to Gift. A second entry point "for discoverability"
   * is exactly the duplication the last three revisions kept producing.
   *
   * ## A SHEET, not a dropdown menu
   *
   * `presentation='sheet'` — so this is a bottom sheet at 375 and, via
   * `DesktopSheetContext`, a centred dialog at md+. This app is drawer-based and is
   * opened rarely (you tap a tag with a phone; you are not living in a console), so
   * the rows want room for an icon, a full label and, later, a line of description.
   * A dropdown pinned to a 40px kebab gives none of that.
   */
  /*
   * AVAILABILITY — working-conventions §46: record state HIDES, permission disables.
   *
   * Both of these are record state, so both are `visible`, never `disabled`: there
   * is no sentence to put in a tooltip that the user could act on, and §46's
   * forcing function says an item you cannot explain is an item to hide.
   *
   * The two predicates are stated SEPARATELY even though they currently collapse to
   * the same boolean, because they answer different questions and will diverge the
   * moment a third action lands:
   *   - Gift is impossible unless the certificate is idle and owned — a certificate
   *     already mid-gift is `waiting_accept`/`transfer_initiated`, not `active`, and
   *     `Cancel gift` (the resolving action §46 requires) is on the transfer card.
   *   - Report lost or stolen is impossible when it is ALREADY lost or stolen —
   *     again not `active`, and `Report found` (the resolver) is on the missing card.
   *
   * So every hidden case here has its resolving action reachable elsewhere on the
   * page, which is the obligation hiding creates.
   */
  const isIdleOwned = isOwnerView && certificate?.status === 'active';

  /**
   * VERA-304 (A1) — is there an evidence level left to earn on this ownership?
   *
   * The completed transfer's `completion_method` is the evidence ladder
   * (`transferred` < `claimed` < `scan_claimed`); `scan_claimed` means the owner
   * has already proven physical possession by scanning the tag, so there is
   * nothing to do and the action HIDES (§46 — record state hides rather than
   * showing a disabled row nobody can act on). This is also what makes the
   * affordance self-clearing: the mutation invalidates the detail query, the
   * refetched `completion_method` comes back `scan_claimed`, and the tile is gone.
   *
   * A NULL method deliberately does NOT offer it. Null means either no transfer
   * at all (a first owner who never received one) or a legacy row predating the
   * column — and in the legacy case the app cannot tell whether the tag was
   * scanned at the time. Offering "Verify possession" for a transfer we have no
   * record of would present a question the data cannot frame; the server would
   * accept the upgrade, but the user has no way to know what they are correcting.
   *
   * The server is the authority regardless: it selects the latest SUCCESSFUL
   * transfer itself and refuses a non-owner. This predicate only decides whether
   * to offer the action, not whether it may happen.
   */
  const completionMethod = certificate?.transfer?.completion_method ?? null;
  /**
   * VRG-494 — `can_verify === false` WITHDRAWS THE OFFER ENTIRELY, and it is the
   * first clause because it outranks every other consideration here.
   *
   * A Connect / NTAG 213 chip carries no SDM cryptography, so there is no
   * verifiable scan it can produce — the possession scan cannot succeed on this
   * item, at any evidence level, ever. Before this clause the card offered it
   * anyway, and the owner discovered the truth by walking to the item, tapping
   * their phone against the tag, and having nothing happen. That is the worst
   * available place to learn it: the failure looks like a broken tag rather than
   * a feature that does not apply.
   *
   * HIDDEN, not disabled-with-a-tooltip (§46 — record state hides). The rule's
   * usual test is "can the user resolve this?", and here nobody can: the chip is
   * the chip, and no admin, no setting and no later action changes its silicon.
   * §46's own carve-out is the precedent — a capability nobody in that context
   * can EVER hold is hidden, because a tooltip promising a path to access would
   * be a false promise. "This tag cannot be verified" is a sentence we could
   * write, but it names a permanent property of the object rather than a step,
   * so as a disabled row it would be a tombstone.
   *
   * `!== false`, not `=== true`: `can_verify` is NULL when the certificate has no
   * tag linked or the family is not derivable (see `CertificateDataAggregate.canVerify`).
   * Null is "unknown", and withdrawing the action on unknown would silently strip
   * the prompt from every unlinked certificate — a much larger set than the one
   * this task is about. Only a POSITIVE "this tag cannot verify" suppresses it.
   *
   * ⚠️ ONE PREDICATE, TWO SURFACES. This same value gates the state card
   * (`stateCards`) and `AuthenticityDrawer`'s "Verify possession" block, exactly
   * as the drawer's own prop doc requires ("must be the caller's
   * `canVerifyPossession`, verbatim"). Adding the clause here therefore removes
   * the drawer CTA too, and a second, hand-written check at either call site is
   * what would let one surface advertise something the other has withdrawn.
   */
  const canVerifyPossession =
    certificate?.can_verify !== false &&
    isIdleOwned &&
    (completionMethod === TransferCompletionMethod.Transferred ||
      completionMethod === TransferCompletionMethod.Claimed);

  /**
   * VRG-494 — the hero badge's verdict, or NULL for "render no badge at all".
   *
   * ## The state that had to go: "Not verified" on a tag that can never verify
   *
   * On a `can_verify === false` tag, "Not verified" is not a status — it is an
   * accusation the reader cannot answer. The label's whole design (see
   * `AuthenticChip`) is that it names a gap the owner can close by tapping the
   * tag, and here there is no tag that will ever close it. So it reads as
   * "something is wrong with your item" and offers no way out.
   *
   * ## What replaces it, and why not a "Connect tag" badge
   *
   * NOT a tappable badge naming the tag type. That was considered and is
   * already settled AGAINST, twice over, in code that predates this task:
   * `AuthenticChip`'s `connected` docblock rejects the word outright — "'Connect'
   * was rejected too: that is the internal capability name, which a person
   * holding the item has never seen and would read as a verdict rather than an
   * event" — and `certificate.drizzle.repository.ts` keeps the chip family
   * server-side precisely so "the family itself never leaves the API". Naming the
   * silicon to a customer is a rule this page does not get to reverse.
   *
   * So the badge is chosen by HOW THIS VIEW WAS REACHED, which is the only thing
   * the badge has ever reported:
   *
   * | reached by | badge |
   * |---|---|
   * | tapping the tag (`connected`) | **`Tag scanned`** — kept |
   * | a shared or saved link        | **nothing** |
   *
   * `Tag scanned` SURVIVES because it is an observed fact that was EARNED: this
   * render followed a genuine tap token minted by the physical chip. VRG-252 built
   * that label for exactly this case, to state the fact and claim nothing beyond
   * it, and dropping it would discard true information the reader is entitled to.
   *
   * On a LINK arrival there is genuinely nothing to say. The reader used a link,
   * the tag could not have proven anything anyway, and a badge whose only content
   * would be "a capability this item does not have" is worse than silence.
   *
   * ⚠️ THIS ALSO FIXES A FALSE STATEMENT, not just a tone problem. The predicate
   * it replaces was `can_verify === false ? 'connected' : scanVerified`, which
   * forced "Tag scanned" onto EVERY view of a Connect certificate — including one
   * opened from a link where no tag was anywhere near the phone. The badge was
   * asserting a scan that had not happened.
   *
   * `pending` resolves to null too, so the badge simply arrives when the verdict
   * does — and arrives with its own mount entrance. The alternative (show
   * `connected` optimistically, then withdraw it if the token turns out forged or
   * expired) puts a claim on screen before it is earned, which is the direction
   * this whole component refuses to be wrong in.
   *
   * Costs nothing in layout: the badge's wrapper is `position: absolute`, so its
   * absence does not move the photograph. The hero's bottom scrim stays regardless
   * — `mediaFrameSx` documents that it serves the carousel dots as well as the
   * chip.
   */
  const heroVerdict: ScanVerificationState | null =
    certificate?.can_verify === false
      ? scanVerified === 'connected' || scanVerified === 'verified'
        ? 'connected'
        : null
      : scanVerified;

  /**
   * ONE handler for both the real tap and the dev simulate, so the preview
   * exercises the production request rather than a parallel branch. The only
   * difference is where the token comes from: an NFC redirect, or the dev-gated
   * mint (which the API refuses outside a dev environment).
   */
  const submitPossessionScan = async (token: string): Promise<boolean> => {
    if (!certificate?.id || !token) return false;
    try {
      await verifyPossession({ certificateId: certificate.id, token });
      return true;
    } catch {
      // ScanTag renders its own "No tag detected" state from the false; the
      // server's reason is deliberately generic and adds nothing here.
      return false;
    }
  };

  /*
   * LABEL, ICON and ORDER now come from `certificateActionItems` — the one
   * declaration this page shares with the certificates list's bulk bar.
   *
   * They were two hand-maintained lists, and they had already diverged as far as
   * two lists can: this page offered Gift and Report lost or stolen, while the
   * bar offered a single "Transfer" — an action the app does not have — whose
   * handler was a `TODO` that silently did nothing. Re-typing this array over
   * there would have fixed today and reset the clock; the shared source is what
   * stops the next action landing on one surface only.
   *
   * What stays HERE is availability, because it is genuinely per-surface: this
   * page asks the question of one record (`isIdleOwned`), the bar asks it of a
   * selection (`utils/certificateActions`). `sell` is deliberately not passed —
   * an action absent from the map is omitted, so declaring it shared does not
   * force this page to advertise it.
   *
   * TODO(VRG-493-App.Certificate.Actions.Overflow): this row is a 3-column grid and
   * at 375px it is EXACTLY full — 3 x 106.3 + 2 x 12 = 342.9 in a 343px row, zero
   * slack. So the moment a FOURTH action lands here, read that task before choosing a
   * layout. The decisions are already made and the reasoning is not obvious:
   *   - 4-6 actions WRAP, left-aligned. Never centre the last row, not even the 5-item
   *     (3+2) case: centring puts tile 4 between columns, and it reflows both tiles
   *     when a 6th arrives, where left-aligned just fills the empty slot.
   *   - Past 6, a "More" tile that EXPANDS IN PLACE. NOT a bottom sheet: Gift and
   *     Report already open drawers, so a sheet-based More makes every action one
   *     level deeper and stacks three sheets, which is exactly what §52 forbids and
   *     was written about this very flow.
   *   - Horizontal scroll was considered and rejected. With zero slack the next tile
   *     shows 0px of peek, so the scroller is invisible rather than subtle.
   */
  const manageItems: CertificateActionItem[] = buildCertificateActionItems({
    /*
     * VERA-304: `verifyPossession` IS NOT HERE ANY MORE, and its absence is the
     * point rather than an omission.
     *
     * It was a fourth tile in this row. It is now a STATE CARD (see `stateCards`
     * below and `VerifyPossessionCard`), because this row holds CAPABILITIES — the
     * standing, repeatable things an owner may do whenever they like — and
     * verifying possession is a TO-DO: outstanding, terminal, self-clearing. A
     * to-do filed among capabilities is why someone who read "Not verified" on the
     * badge had no reason to look here for the remedy.
     *
     * The row is therefore back to exactly three actions, which is
     * `ActionTileRow`'s `TILES_PER_ROW` — one line of 106x106 squares, no wrap and
     * no `More` tile. Adding a fourth capability here is legitimate; adding another
     * to-do is not.
     */
    gift: {
      onClick: () => setOpenGiftCertificate(true),
      visible: isIdleOwned,
    },
    /*
     * `sell` IS passed now, where the note above says it deliberately was not.
     *
     * That decision was made when this page's only surface for these actions was
     * a sheet reached through a kebab — a list where an inert "Coming soon" row
     * is pure cost, since you had to open the sheet to find out it was inert. The
     * tile row is the opposite: the actions are on the page, so the shape of the
     * set is visible without a tap, and OMITTING sell would say the certificate
     * has two things you can do with it when it has three, one of which is not
     * ready. The defs file already argues at length that "Coming soon" is an
     * honest signal rather than a false promise; that argument only pays off on
     * a surface where the row is seen.
     *
     * Same `isIdleOwned` gate as its neighbours, and not a weaker one: selling a
     * certificate you have reported stolen is exactly as impossible as gifting
     * it, and a tile that survives into the missing state would advertise a
     * capability that will be state-gated the day it ships.
     */
    sell: {
      onClick: () => {
        setSellOpenCount(n => n + 1);
        setOpenSellMethod(true);
      },
      visible: isIdleOwned,
    },
    reportMissing: {
      onClick: () => setOpenReportMissing(true),
      visible: isIdleOwned,
    },
  });
  // An overflow trigger that opens an empty sheet is worse than no trigger, so the
  // kebab itself is gated on the set, not on the view.
  const hasManageActions = manageItems.some(item => item.visible !== false);

  /**
   * VERA-315 — every "something is going on with this certificate" state, in ONE
   * ranked list. `StateCardStack` renders the most severe one as a full card and
   * folds the rest into a single row; see that file for the ranking's justification
   * and for why DOM order here must stay fixed.
   *
   * DECLARATION ORDER IS THE DOM ORDER and it never varies — every entry is always
   * present, `active` is what changes. That is not cosmetic: the transfer card owns
   * the scan-tag and "Gift canceled" drawers, and a mutation that succeeds is the
   * same event that retires the card, so an entry that came and went from this array
   * would unmount the success drawer at the moment it was meant to open.
   *
   * The order below is coincidentally the rank order, which is convenient to read
   * but load-bearing for nothing — `severity` is the only thing that decides
   * position.
   */
  const stateCards: StateCardItem[] = [
    {
      key: 'missing',
      severity: STATE_SEVERITY.MISSING,
      // Reads from `lastMissingStatus`, like the card itself, so the summary row
      // does not go blank mid-collapse after "Report found".
      label: lastMissingStatus === 'lost' ? 'Lost' : 'Stolen',
      icon: <OctagonAlert size={16} strokeWidth={1.75} />,
      /*
        `isMissing`, NOT `lastMissingStatus`.

        The two diverge for exactly the length of the exit animation, and that is
        the point: `active` goes false the instant the status flips to 'active', so
        the stack's `Collapse` animates the card away in place (VERA-301's original
        behaviour, previously a `Collapse` owned right here) while `card` below
        keeps rendering the state it is closing rather than going blank.

        It also carries the OWNERSHIP gate — `isMissing` requires `isOwnerView` —
        which is what makes it safe for this card to have moved OUT of the
        `viewType !== PUBLIC || isAccessDataCollected` block it used to sit in.
        `isOwnerView` is `viewType === OWNER`, so it implies that condition; the
        gate was redundant for this one card and is preserved for everything else
        still inside it.
      */
      active: isMissing,
      card: lastMissingStatus ? (
        /*
          ONE card for the whole missing state, for BOTH statuses, holding its own
          actions.

          This used to be a stolen-only card followed by a separate, bare `Report
          found` button in its own bordered box. Two problems: a lost certificate
          got a naked button with nothing naming the state it resolved, and even on
          stolen the button sat OUTSIDE the card that explained why it was offered.
          Moving the action inside (2026-08-22, user directive: temporary-state
          buttons live in the block) means `lost` now renders the card too —
          `MissingStateCallout` already supported it ("Found your item?", no body),
          it was just never mounted.

          `onReportFound` is passed only while `isMissing`, deliberately NOT while
          `lastMissingStatus` — that outlives the status change so the card can
          animate away in place, and keying the action off it would keep offering
          "Report found" for an item already found, during the collapse.

          Share is stolen-only by design; see the note in MissingStateCallout.
        */
        <MissingStateCallout
          status={lastMissingStatus}
          onShare={lastMissingStatus === 'stolen' ? handleShare : undefined}
          onReportFound={isMissing ? () => setOpenConfirmStatusChange(true) : undefined}
        />
      ) : null,
    },
    {
      key: 'suspicious',
      severity: STATE_SEVERITY.SUSPICIOUS,
      label: 'Suspicious activity',
      icon: <ShieldAlert size={16} strokeWidth={1.75} />,
      // Ungated by `viewType`, which is what the Alert this replaces did too: the
      // scan-log endpoint is public, and a possible counterfeit is the one thing a
      // stranger scanning this tag has the most reason to be told.
      active: hasInvalidScan && !invalidScanNoticeDismissed,
      card: (
        <SuspiciousActivityCard
          place={invalidScanPlace}
          /*
            Opens the Scans screen ON the offending scan — panned to, pulsing, and
            in the docked card — rather than on the full spread and leaving the
            reader to find it. Suspicious activity is not actionable by the owner
            beyond being notified, so "show me exactly which scan" is the whole of
            what this control can usefully do.

            Best effort by construction: a scan with no coordinates has no pin, and
            the screen then opens unfocused. That is why the button says "Review
            scan" and not "View on map" — see `MapHistory`'s `focusScanId`.
          */
          onReviewScan={() =>
            openSubView('scans', { focusScanId: latestInvalidScan?.id })
          }
          onDismiss={() => setInvalidScanNoticeDismissed(true)}
        />
      ),
    },
    {
      key: 'transfer',
      severity: STATE_SEVERITY.PENDING_TRANSFER,
      // The card's own heading, from the card's own string table (§36) — the
      // collapsed row must call a pending gift what the expanded card calls it.
      label: CARD_HEADING[transferType ?? ''] ?? 'Transfer pending',
      // A gift box for a gift; the outgoing-transfer glyph `StatusNotification`
      // already uses for a primary transfer. Same glyph for the same state across
      // surfaces.
      icon:
        transferType === 'transfer-in' ? (
          <UserRoundMinus size={16} strokeWidth={1.75} />
        ) : (
          <Gift size={16} strokeWidth={1.75} />
        ),
      active: showsTransferCard,
      // ⚠️ Rendered whether or not it is active — see the note at the top of this
      // array. When there is no pending transfer this produces nothing visible; it
      // is here to stay mounted, not to be seen.
      card: <TransferSummarySection certificate={certificate || null} />,
    },
    {
      /*
       * VERA-304 — the possession-verification prompt, which used to be a TILE.
       *
       * It moved here because Gift / Sell / Report are CAPABILITIES (standing,
       * repeatable, chosen whenever the owner likes) while this is a TO-DO
       * (outstanding, terminal, self-clearing) — see `VerifyPossessionCard` for the
       * full argument, including why "it appears once and never returns" is not an
       * objection when the Gift-received card on this same page does exactly that.
       *
       * `active` is the SAME `canVerifyPossession` the authenticity drawer gates its
       * own offer on, so the two routes to this action cannot disagree.
       */
      key: 'verifyPossession',
      severity: STATE_SEVERITY.VERIFY_POSSESSION,
      label: 'Verify possession',
      icon: <BadgeCheck size={16} strokeWidth={1.75} />,
      active: canVerifyPossession,
      card: <VerifyPossessionCard onVerify={() => setOpenVerifyPossession(true)} />,
    },
  ];

  return (
    /* Desktop's arrival transition, matching the platform: the page fades in over the
       footprint `CertificatePageSkeleton` just reserved. `ContentFade` is a NO-OP below
       900px, which is what keeps the mobile push below untouched — the two would
       otherwise both animate this same element and the page would arrive translucent
       while it slid.

       Safe outside the slide rather than inside it for the same reason: `Fade` sets
       opacity, never a transform, so it does not become the containing block for
       `StickyActionBar` (`position: fixed`). That constraint is why the deleted page
       fade was opacity-only too, and why `NavigationHeader` is `sticky`.

       The VERA-315 note that used to sit here — the `DesktopSheetContext.Provider`
       that wrapped this page is GONE, because the behaviour it opted into is now the
       app-wide default (see `DesktopSheetContext`): every `DrawerPopup` here,
       including the ones inside `AuthenticityDrawer`, `SealOpenedDrawer`,
       `ReportMissingDrawer` and the option sheets, still renders as a centred dialog
       at md+ and an unchanged bottom sheet at 375. The provider was redundant AND
       misleading: leaving `value` on one page implies the other 34 files with a sheet
       are deliberately excluded, which was the bug. */
    <>
    {/*
      VRG-790 — outside `ContentFade` and ahead of it, so the reveal is not subject to the
      page's own arrival fade: the two would animate against each other and the reveal
      would arrive translucent over a certificate it is supposed to be hiding. It is
      `position: fixed`, so it does not participate in the slide below either.

      Present in this branch as well as the two loading branches above because a reveal
      that is still playing when the certificate lands must keep the screen until it is
      finished or skipped — dropping it here would cut the animation off at exactly the
      moment the page became ready, which is the common case on a fast connection.
    */}
    {revealOverlay}
    <ContentFade>
    <Stack
      sx={{
        // Exit wins while leaving: two `animation` declarations cannot coexist on
        // one element, and the entry animation has already finished by then.
        /*
         * Entering FORWARD (pushed from the list) comes from the right; entering
         * BACKWARD (returned to from Provenance / Scans / Activity) comes from
         * the LEFT, so the stack unwinds instead of cutting.
         *
         * `slideBack` is set by `usePushPage`'s `goBack` and is deliberately a
         * separate flag from `slideIn` — see `SlideNavState`. Reading only
         * `slideIn` is what made the return animate the child out over 260ms and
         * then hard-cut this page in, which is the "flashes the whole page"
         * report.
         */
        ...(exiting
          ? slideOutSx(true)
          : (() => {
              const navState = location.state as SlideNavState | null
              /*
               * DESKTOP gets the short push too (VERA-315 prototype). Same
               * direction rule as the phone — forward from the right, back from
               * the left — over 24px instead of the full width, so this page
               * stops teleporting while a pointer user is not made to watch the
               * viewport travel. See `pushInSx`.
               *
               * The mobile branch is checked FIRST and is unchanged; the desktop
               * branch can only be reached when `shouldSlide()` is false, so the
               * two can never both arm.
               */
              if (navState?.slideBack) {
                return shouldSlide()
                  ? slideInSx(true, 'left')
                  : pushInSx(shouldPushDesktop(), 'left')
              }
              if (navState?.slideIn) return slideInSx(true)
              // Entered from the LIST on desktop — the list sets `slideIn` only on
              // mobile, so a desktop push arrives here with no flag at all.
              return pushInSx(shouldPushDesktop(), 'right')
            })()),
        background: theme => theme.palette.background.default,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        /*
         * THE GATED VIEW IS ONE SCREEN, so the page gets a DEFINITE height to
         * distribute rather than a floor to grow past.
         *
         * `minHeight: 100vh` alone cannot do it: it only guarantees the page is at
         * least a viewport tall, so `flex: 1 1 0` on the blurred placeholder below has
         * no fixed total to take its share OF, and the placeholder's own content height
         * wins. That is how the gated page ended up 1188px tall on an 812px viewport
         * with the attribution 315px below the fold.
         *
         * `dvh`, not `vh`, and the difference is the whole point on the device this app
         * is actually used on: mobile Safari's `100vh` is the LARGE viewport, measured
         * with the toolbars retracted, so a `100vh` page is taller than what you can see
         * and scrolls by exactly the toolbar height even when its content fits. `dvh`
         * tracks the visible viewport. Browsers without `dvh` (Safari < 15.4) drop the
         * declaration and keep the `100vh` floor above, i.e. today's behaviour.
         *
         * ⚠️ NOT `overflow: hidden`. On a short viewport (a 667px phone) the gate card
         * genuinely does not fit — hero plus card plus footer exceeds it — and the page
         * MUST still scroll there or the primary button is unreachable. A definite
         * height clips nothing by itself; oversized children still make the document
         * scrollable, which is the correct outcome. The placeholder gives its space back
         * first, and only what is left over scrolls.
         *
         * md+ is untouched: the desktop grid sizes the record column by its content
         * (`alignItems: 'start'`, required by the sticky media column), so there is no
         * free space to distribute and a definite height would buy nothing.
         */
        ...(showAccessGate && {
          [muiTheme.breakpoints.down('md')]: { height: '100dvh', minHeight: '100dvh' },
        }),
      }}
    >
      <OwnerPublicPreviewHeader
        viewType={viewType}
        setViewType={setViewType}
        // Arrived from the Share screen (it deep-links with ?previewView=), so go back
        // there rather than dropping the owner on the certificate.
        exitLabel={previewViewSlug ? 'Back to Share' : undefined}
        onExit={
          previewViewSlug
            ? () => navigate(`/certificates/${certificateId}/share`)
            : undefined
        }
      />

      {/* VRG-165: the image leads. A certificate's whole claim is "this object is
          real", so the photograph is the content, not the fourth thing on screen
          behind a bar, an alert and a chip. The controls float over it instead of
          spending a full bar's height above it.

          VERA-315 Phase 7: and once the photograph has scrolled away, the same
          controls gain a glass bar and the product's name — so the page never
          spends height on a title bar while the image is on screen, and never
          leaves you scrolled deep into a certificate with nothing naming it.

          The header is STICKY and carries a negative bottom margin, so it sits in
          the flow (which is what lets it stick) while the hero starts at the top
          of the page underneath it. With no photograph there is nothing to sit
          on: the controls landed on the placeholder AND the Authentic chip, so
          the hero takes the height back as padding. */}
      {/* VERA-304 — the header is NOT owner-only. A pending incoming transfer is
          the one state where a signed-in user reaches this page from their own
          certificates list while `viewType` is still PUBLIC (see
          `isIncomingTransferView`), and gating the whole bar on `isOwnerView`
          took Back away from exactly that person: they arrived from a list they
          could no longer get back to, on a MemoryRouter with no browser chrome
          to rescue them.

          Deliberately NOT widened to "any authenticated viewer". Someone who taps
          a stranger's tag while signed in has no back DESTINATION — their own list
          is a lateral jump, not a return — whereas the transfer recipient has this
          certificate sitting in that list already.

          COSTS NOTHING IN LAYOUT, which is also why the omission reads as an
          oversight rather than a decision: `pageBodySx` reserves `DESKTOP_TOP`
          (bar + gutter) at md+ and `mediaColSx` reserves `NAV_HEADER_HEIGHT` when
          there is no photograph, both UNCONDITIONALLY. The bar's own
          `mb: -NAV_HEADER_HEIGHT` cancels its height in flow. So the space was
          being held open for a bar that never rendered; this fills it. Measured
          the same at 375 and 1280, with and without images. */}
      {(isOwnerView || isIncomingTransferView) && (
        <NavigationHeader
          title={certificate?.product?.title}
          /* VERA-315 — always solid on desktop, and not as a stylistic preference:
             `headerSolid` is measured off the hero scrolling under the bar, and on
             desktop the hero is STICKY, so it never does. Left scroll-driven, the bar
             would stay transparent forever — a dark photo scrim over a white page,
             with the title that the solid state carries never appearing. */
          solid={headerSolid || isDesktopLayout}
          /* VERA-315 — at md+ the controls belong to the same 1120 column as the
             content, not to the viewport: Back lines up with the media column's left
             edge and Share with the record column's right edge, so the bar reads as
             the top of the page rather than as a separate full-bleed strip floating
             above it. Below md these are ignored and the header is untouched. */
          contentMaxWidth={DESKTOP_MAX_WIDTH}
          contentGutter={DESKTOP_GUTTER}
          onBack={handleBack}
          /* SHARE IS OWNER-ONLY, and stays owner-only in the incoming-transfer
             state. Not an oversight and not caution — sharing before you have
             claimed is broken in three separate ways:

               1. It does not work. Share navigates to the Share SCREEN, whose
                  `GET /certificates/customer/:id/views` runs
                  `assertOwnedCertificate` and 404s for anyone but the owner
                  (certificates.customer.controller.ts). The recipient would land
                  on an empty screen. A control that navigates somewhere broken is
                  worse than an absent one.
               2. It is not a share button, it is view MANAGEMENT — create,
                  rename, disable, delete a view, and invite viewers to it. Those
                  are the sender's powers over the sender's record until accept.
               3. A share link asserts "this is mine". Before the claim it is not,
                  and this app's whole subject is not making claims that are not
                  true.

             `undefined` genuinely SUPPRESSES the button (see NavigationHeader),
             so the recipient gets Back and no dead glyph beside it. */
          /* VRG-778: the owner gets their Share SCREEN; everyone else gets the
             native sheet. Previously `undefined` for a non-owner, so a public
             visitor had no share affordance at all — which is what this fixes. */
          onShare={isOwnerView ? handleShare : handlePublicShare}
          /* The Manage overflow. Share stays OUT of it and keeps its own control:
             it is the frequent, non-mutating action, and it is the one thing a
             certificate is FOR after you own it. The kebab is the mutating set.

             `undefined` when the set is empty, which is what suppresses the button —
             see `hasManageActions`. */
          /*
            NO HEADER TRIGGER. The in-page `Actions` control below opens this same
            sheet, and two doorways onto one surface is the duplication the last
            several revisions of this page kept producing — the kebab was also the
            LESS discoverable of the two, since its label only rendered from `md`
            up and a phone saw a bare ellipsis.

            Removing it also gives the photograph back a third of its chrome: the
            overlay is now back + share, two controls rather than three glass
            chips competing with the product.

            `hasManageActions` still gates the in-page button; it simply is not
            gating anything here any more.
          */
          overlay
        />
      )}
      {/* VRG-825 — the acquisition-demo disclosure, ABOVE the brand's own mark.

          THIS IS ITS THIRD POSITION, and the first two are why this comment is long.
          It began as a near-black slab between the hero and the product name
          ("this looks horrible"), became a one-line white bar with a chevron in the
          same slot (VRG-309), and now moves out of the record column entirely.

          ── Why above the LOGO BAR specifically, and not merely "higher" ─────────
          An acquisition demo is provisioned by SCRAPING the prospect's public site —
          logo included. So `CertificateBrandHeader`, the bar immediately below this
          one, is ITSELF demo data. A disclosure sitting under it implicitly exempts
          it: the page reads "real brand, demo content", when in fact the mark is a
          guess too. Above the logo the claim is literally true — everything below
          this line, branding included, is a demo.

          That also makes it the standard shape for this kind of bar. A staging
          banner, an impersonation bar and a consent bar all sit above the app's own
          chrome, because they describe the CONTEXT rather than the content. This one
          does the same thing and now sits where that class of bar sits.

          ── What this gives up, stated rather than glossed ──────────────────────
          The previous position was argued FOR, not stumbled into: "the hero is the
          moment that gets the meeting … above the hero it would blunt the tap." That
          cost is real and is accepted here. Two things make it affordable:

            1. This bar is `position: sticky` and ALWAYS WAS. The moment the prospect
               scrolls at all it pins to the top of the viewport — above the hero,
               above the logo. So the old placement only ever differed on the FIRST
               PAINT; from the first scroll the two are identical. Moving it makes
               the resting state agree with the state it spends most of its life in.
            2. The page is entered by TAPPING A CHIP, so the top of the page is the
               first thing seen. A disclosure there is not buried, it is first.

          ── Why it stays a bar and not a chip beside AuthenticChip ──────────────
          Do not re-propose the chip; it has now been rejected twice, and the second
          reason is stronger than the first. `AuthenticChip` is a two-state TRUST
          indicator (Authentic / Not verified) whose "Authentic" state is earned by a
          valid scan — and a demo certificate with a real chip bound to it (VRG-388
          "Add tag") scans valid. A DEMO pill beside it would put "Authentic" and
          "DEMO" in matching treatment on one photograph: the page asserting verified
          and not-real at once, in one visual language. That is worse than a bar, not
          merely busier.

          The certificate page is PUBLIC (`@OptionalAuth`, and VRG-161 notes it serves
          anonymous scanners), so the disclosure has to hold up for someone who gets
          the URL second-hand and never met an AE. That is why the claim stays legible
          AT REST and only the explanation sits behind the chevron.

          ── Placement mechanics ────────────────────────────────────────────────
          A direct sibling of `CertificateBrandHeader` at the page-body level, so it
          is full-bleed for the same reason that bar is — no padded wrapper, edge to
          edge at 375 (§37, the only width that matters here).

          `stickyTop` is UNCHANGED and still decided here, because only this file
          knows what else is pinned. `NavigationHeader` is sticky at `top: 0`,
          `NAV_HEADER_HEIGHT` tall, `z-index: 1100`, and renders ONLY for the owner
          and the incoming-transfer recipient — it is still ABOVE this in the DOM, so:

            owner / incoming transfer → pin below the glass bar
            everyone else, incl. the cold prospect → pin to the very top

          Passing 0 unconditionally would not merely misalign it: 1100 beats this
          bar's `z-index: 3`, so the disclosure would slide UNDER the header and
          vanish for the one viewer who has one.

          `CertificateBrandHeader` needs no such accounting — it is plain in-flow at
          `NAV_HEADER_HEIGHT` with no `position` and no `z-index`, so it simply
          scrolls underneath this bar rather than competing with it.

          `audience` picks the COPY, and the hostname is the signal because the
          `-demo` subdomain IS the cold-outbound rail (CHECK-constrained by
          `companies_demo_subdomain_rail`) — so being off it is not a proxy for
          takeover, it is takeover. */}
      {isDemoCertificate && (
        <DemoBanner
          /* VRG-825 — NAME THE CERTIFICATE'S OWN COMPANY, not the hostname's.
             `brandName` is first in the component's fallback chain and this call
             site never passed it, so the name came from
             `useGetCompanyBySubdomain(extractSubdomain())` — the HOST, not the
             record. Those agree only while a demo certificate is viewed on its own
             `-demo` subdomain. Off it they diverge, and the banner then names
             whatever brand the host resolves to.

             Measured, not theorised: certificate 3bdc3d2d (company Allbirds,
             `allbirds-demo`) rendered "not issued by Louis Vuitton" — a real,
             unrelated `kind: standard` company, because that is what the ambient
             host resolved to. A public page (`@OptionalAuth`) disclaiming the wrong
             luxury brand on someone else's product is worse than no disclaimer.

             The three divergent cases are all real: a shared link opened on the
             canonical app domain, an embed on the brand's own site, and
             post-takeover — the component's own note says "there is no `-demo` slug
             off a demo host", which is this same gap seen from the other side.

             `certificate.company` is the row's OWN issuer, so it is right in every
             one of them, including post-takeover where "not issued by <brand>"
             stays true (Vera seeded it, the brand did not). The existing chain is
             untouched below this and still covers a missing name. Safe to pass
             raw: only the SLUG branch is title-cased, so an API display name is
             used verbatim. */
          brandName={certificate?.company?.name}
          audience={isDemoHostname ? 'prospect' : 'customer'}
          stickyTop={isOwnerView || isIncomingTransferView ? NAV_HEADER_HEIGHT : 0}
        />
      )}

      {showBrandHeader && <CertificateBrandHeader company={certificate?.company} />}
      <Box sx={pageBodySx(showBrandHeader)}>
      {/* `hasHeroMedia`, not main's `hasImages`: VRG-749 renamed the flag so a model
          counts as hero media, and `hasImages` no longer exists. See `mediaColSx`. */}
      <Stack ref={heroRef} sx={mediaColSx(hasHeroMedia, showBrandHeader)}>
      {/* The photograph and the chip on it — see `mediaShotSx` for why this box
          exists rather than the chip anchoring to the column. */}
      <Box sx={mediaShotSx}>
        {/* Moved off the top-left, which the back button now occupies. Bottom-left
            suits it better anyway: it is a status, not a control, so it does not need
            a thumb-reachable corner. */}
        {heroVerdict !== null && (
        /* `bottom: 16` is measured from the PHOTOGRAPH'S bottom edge, which is also
           this box's — see `mediaShotSx`. It was 32 while that box carried `pb: 16px`;
           both numbers describe the same 16px above the image, and they have to move
           together, so do not change one without the other. */
        <Box sx={{ position: 'absolute', bottom: 16, left: 16, zIndex: 1 }}>
          <Stack direction='row' spacing={1}>
            {/* The badge animates its own entrance (circle → pill → label) on mount, and
                the scan loader just fades out independently. They used to be one
                shared-element flight; the coupling — a ref threaded out of this page into
                an out-of-React module — went with it. See components/UI/AuthenticChip. */}
            <AuthenticChip
              verified={heroVerdict}
              onClick={() => setOpenAuthenticity(true)}
              /* The ONLY call site that floats on the photograph, so the only one that
                 takes the white fill — see `AuthenticChip`'s `onImage`. The other two
                 sit on white cards, where white would erase the chip. */
              onImage
            />
            {/* VERA-304: the `TransferCompletionChip` THAT STOOD HERE IS GONE. The
                component survives and still renders in Provenance → Current owner;
                only this second, unlabelled instance is removed.

                It said "Transferred" / "Claimed" / "Scan verified" — the ownership
                EVIDENCE LADDER, which is real information — but three things made
                the hero the wrong place for it:

                 - It was UNEXPLAINED and INERT. No label, no tap target, no way to
                   learn what "Transferred" meant from where it sat.
                 - It DUPLICATED the Provenance row, which renders the same chip
                   under the words "Verification" beside the owner it describes.
                   That instance has context; this one had none.
                 - It sat beside `AuthenticChip`, which reports a DIFFERENT fact —
                   how this page view was reached (a tag tap this session vs a
                   link). Two adjacent pills in one visual language saying two
                   unrelated things is worse than either alone.

                The remedy for a low evidence level is now the state card below
                (`VerifyPossessionCard`), which explains itself and offers the
                action — which is what a hero pill could never do. */}
          </Stack>
        </Box>
        )}
        <Box sx={mediaFrameSx}>
          {/*
            VRG-749 — the MODEL takes the hero when the product has one.

            This is the surface the whole feature exists for. Until now the largest
            element on a provenance page was the brand's stock photography: a
            DIFFERENT unit, under studio lighting, in perfect condition. A model of
            the product, which the holder can turn in their hand on screen and place
            in the room, is what the page is actually about.

            FOOTPRINT PARITY is why it is sized by RATIO (§19). `ImageCarousel` sizes
            itself `h-[100vw]` — square at the phone's width — and at md+ this frame
            pins `aspect-ratio: 1/1`. A model has no intrinsic height at all, so
            without a square it would collapse at phone width and the page would
            reflow the moment it loaded. `aspectRatio='1 / 1'` matches the carousel at
            both widths; a wrapper + `height="100%"` does NOT, because the component's
            root is auto-height and a percentage height has nothing to resolve
            against — see the prop's own note.

            The photos are NOT discarded — `images` still holds them and the brand
            still manages them on the edit form. The hero simply leads with the better
            of the two when both exist.
          */}
          {certificate?.product?.model_url ? (
            <ProductModel
              src={certificate.product.model_url}
              title={certificate?.product?.title}
              /* The RATIO, not a height. ImageCarousel is h-[100vw] (square at the
                 phone's width) and the md+ frame pins aspect-ratio 1/1, so this
                 matches both — and a percentage height collapses to zero here,
                 because this component's root is auto-height. */
              aspectRatio='1 / 1'
              /* Full-bleed on the page's own background, not in a frame: on a phone
                 this is edge-to-edge and the product should read as the page's
                 subject rather than as a thumbnail inside a card. The platform
                 product page takes `panel` instead, where it sits among bordered
                 cards. */
              surface='bare'
              /* The hint line is suppressed HERE and only here: it would sit between
                 the hero and the record column, and `AuthenticChip` already floats in
                 this box. A model that is visibly rotating is also its own invitation
                 to touch it, which is not true of the static panel on the edit form. */
              showHint={false}
              /* The RESET control, added deliberately against this prop's original
                 default -- see the `controls` docblock, which used to argue the hero
                 did not want it.

                 The argument that lost: AR is "already the right affordance here". It
                 is a DIFFERENT affordance. AR leaves the page for the OS viewer and is
                 absent on every desktop (`canActivateAR`), so on a laptop the hero had
                 no way back at all once someone had dragged it, and the idle spin
                 resumes from wherever they left it rather than from the pose the model
                 loaded in.

                 The argument that won is consistency: the brand panels below already
                 render this control, so a reader who learned the gesture there found
                 the hero -- the LARGEST model on the page -- to be the one that could
                 not be put back. One object behaving two ways depending on which block
                 it landed in is the same defect this file's `autoRotate` note records
                 fixing in the other direction. */
              controls
            />
          ) : (
            <ImageCarousel images={certificate?.product?.images} fit='cover' />
          )}
        </Box>
      </Box>
      {/* The MANAGE group used to hang here at md+ (and last in the record column
          below md). Both are gone: the actions are in the header's kebab sheet now,
          so the media column is the photograph and nothing else, at every width.
          See `manageItems`. */}
      </Stack>

      {/* The record column. A plain flex column at phone width — identical to what
          these three were as direct children of the page's own flex column — and the
          grid's second track at md+. */}
      <Box sx={recordColSx}>


      {/* The "Not verified in this session" callout was REMOVED here (it sat between
          the carousel and the invalid-scan alert). It restated the badge on the image
          directly above it, in amber — grading as a warning what the drawer it opens
          calls normal ("that is normal for a shared or saved link"), so two of our own
          surfaces disagreed about severity. Its instruction was also unfollowable in
          the common case: someone opening a shared link has no tag to tap. The badge
          carries the status, and now carries a chevron so the explanation stays
          discoverable without a second block restating it. */}
      {/* VERA-315: the "Suspicious activity detected" ALERT THAT STOOD HERE IS GONE.

          It was a bespoke MUI `Alert` rendered above the product title — above the
          name of the object it is about, and above every other state the certificate
          can be in. Nothing chose that hierarchy; it was added last and there was
          room at the top, which meant the page's least certain signal (an invalid
          scan is EVIDENCE, not a confirmed state) outranked a certificate the owner
          had actually reported stolen.

          It is now a state card, ranked among the others by `StateCardStack` below
          the title. See `SuspiciousActivityCard`. */}
      <Box sx={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column' }}>
        {/* Clearance above the product title. The value is authored in px because
            it is DERIVED FROM A MEASUREMENT, not chosen on the spacing scale.

            ── Mobile: 27px, which renders as a 32px VISUAL gap ─────────────────
            The complaint was that the demo disclosure bar sits too close to the
            product's name. It did: measured at 375×812, the distance from the
            bar's bottom hairline to the TOP OF THE TITLE'S GLYPHS was 21.2px.

            That is not the CSS value and could not be, which is the trap
            working-conventions §40 warns about literally: a 24px title on a 28.8px
            line box carries half-leading above it, and the cap height sits below
            the ascender, so ~6.2px of the gap the eye reads is contributed by the
            font rather than by the padding. Hence 27 -> 32.2 visual, iterated in
            the browser rather than assumed from `32 - 21`.

            32 is the app's own header rhythm (§40: back-link -> title 12,
            title -> description 16, description -> content 32; a section heading
            sits ~29px off its first row), so this puts the title in the same
            rhythm as everything below it instead of at a value of its own.

            IT IS ON THE TITLE BLOCK, NOT ON THE BAR, and that is the load-bearing
            choice. `DemoBanner` is full-bleed shared chrome that also sits at the
            TOP of this column at md+; giving it a bottom margin would make its own
            spacing conditional on whatever happens to follow it. The clearance
            belongs to the title's header block, which owns its top space at every
            breakpoint and in every page state.

            VRG-825 — THIS IS NOW THE ONLY CASE, not collateral. The measurement
            above was taken when `DemoBanner` sat directly above the title; it has
            since moved above `CertificateBrandHeader`, so on a demo page the title
            follows the PHOTOGRAPH exactly as it always did on a non-demo one. That
            gap was independently the same 21.2px and takes the same 32, which is
            why the value survives the move unchanged rather than needing re-deriving
            — one number for both, instead of a demo-only exception. It had been 38 until
            `5cdf417df` removed `mediaShotSx`'s `pb: 16px` to sit the bar flush
            under the photograph — a change measured only in the banner case, so
            the 16px it took off the non-demo photograph→title gap was never
            looked at. One value now serves both.

            ── md+: unchanged at 32 (`pt: 4`) ───────────────────────────────────
            VERA-315 doubled it there because the record column starts 24px under
            a solid glass bar and the title read as attached to the chrome
            (measured 40px -> 56px from the bar's bottom edge). That is a different
            complaint with its own measurement; it is deliberately not disturbed,
            and the longhand `pt` above it is overridden by this media query. */}
        {/* `flex: 1 1 auto` ONLY while the gate is up, and only below md. Every other
            ancestor in this chain — the page Stack, `pageBodySx`, `recordColSx`, the Box
            above — is already a `flex: 1 1 auto` column, so this Stack was the one link
            that did not stretch, and a `flex: 1 1 0` child inside a non-stretching parent
            has no free space to claim. `minHeight: 0` is what lets it actually shrink
            below its content: a flex item's default `min-height: auto` would hold the
            placeholder's authored height and re-create the overflow this removes.

            Gated-only so the ungated page is byte-identical — real certificate content is
            longer than a viewport and must keep scrolling normally. */}
        <Stack
          sx={theme => ({
            p: 2,
            pt: '27px',
            ...(showAccessGate && {
              [theme.breakpoints.down('md')]: {
                flex: '1 1 auto',
                minHeight: 0,
                // `p: 2`'s BOTTOM 16 goes, and this is not just reclaiming height. The
                // blurred placeholder is now the last thing in the column and its whole
                // claim is that content continues under the fold; a 16px band of page
                // background between it and the attribution contradicts that — it draws
                // the end of the content exactly where the curtain is meant to hide it.
                // The left/right gutters are untouched.
                pb: 0,
              },
            }),
            [theme.breakpoints.up('md')]: { pt: 4 },
          })}
        >
          {/* ── TITLE ON ITS OWN LINE; THE SEAL BADGE BELOW IT (VRG-720) ────────
              This was a `direction='row' justifyContent='space-between'` split with
              the title left and a status column right — "the same identity-left /
              status-right split the scan rows use" (VERA-476). That split existed to
              hold TWO badges. One of them is gone (see below), and the survivor reads
              better underneath, so the row is gone with it.

              The decisive number is not the badge's width, which is why this looked
              like a fair trade for so long. "Opened" measures 69px, but it cost the
              title 133px of the 343px content width — 39% of the line — because the
              row's `spacing={3}` gap plus right-alignment reserve far more than the
              glyph occupies. Moving it below took the title from 210px to 336px, and
              "Omega Speedmaster Moonwatch" from two lines to one.

              It is also NET SHORTER vertically, which is the opposite of what a
              stacked layout usually costs: one title line plus a badge line is less
              than two title lines with a badge sharing the first.

              And it makes the layout STABLE. In the row, every title was sized by
              whether a seal happened to be open, so two certificates with the same
              product name wrapped differently. Now the title always gets the full
              width and the badge never changes it. */}
          <Box sx={{ mb: '16px' }}>
            <ProductPageHeader certificate={certificate} showStatus={false} />
            {/* ── THERE IS NO OWNERSHIP-STATUS BADGE HERE (VRG-720) ──────────────
                  `ProductStatusBadge` used to render here under
                  `!isMissing && !showsTransferCard && status !== 'active'`. Removed,
                  and the component with it — this was its only call site. The list
                  card is unaffected: `CertificatesTeaser` calls
                  `getCertificateStatusBadgeProps` directly, and a brand's own list is
                  their own data, so none of the reasoning below applies to it.

                  WHY, and this is a privacy decision rather than a layout one. VERA-301
                  had already narrowed the badge to "states that need explaining", and
                  VERA-304 moved the transfer statuses onto the transfer CARD. Between
                  them they left the badge reachable in exactly two cases:

                   1. A viewer who is NOT party to the transfer. `useTransferCardState`
                      resolves `transferType` only when the signed-in profile matches
                      the transfer's `fromCustomer` or `toCustomer`, so for a stranger
                      who scanned the tag the card never renders and the badge was the
                      only channel left. That is the case being deliberately closed: an
                      anonymous scanner learning that this item is mid-handover is a
                      disclosure about the OWNER's activity, not a fact about the item.
                      It is the same class of leak `StatusNotification` records fixing
                      when a gift row rendered "A gift from <name>" to the wrong viewer,
                      and the whole reason `useTransferCardState` gates on identity in
                      the first place. Leaving the badge up re-opened by the side door
                      what that hook closes by the front.
                   2. `inactive`, which has no card. Unreachable today — nothing in the
                      product can set an OWNED certificate back to inactive (the
                      use-case has no ownership guard, but the mutation calling it has
                      zero UI callers anywhere in the platform).

                  ⚠️ So the standing instruction from the old comment SURVIVES this
                  removal and is now the only record of it: if a deactivate action is
                  ever wired up, `inactive` needs its own STATE CARD before that ships.
                  Not a badge restored here — a card, like every other explained state.
                  A badge would reintroduce case 1 along with it. */}
              {/* VERA-628: the "Opened" badge is now the ONLY tamper signal (the standing
                  Alert above is gone) — tapping it opens the SealOpenedDrawer sheet.

                  `dot`, NOT the default `chip`. The original reason was adjacency —
                  `TagOpenedBadge`'s own docblock makes it about the certificates LIST
                  card, where it sits beside a `dot` ownership status and a pill "put
                  two different status grammars in one row". With the ownership badge
                  gone and this one no longer in a row at all, that argument no longer
                  applies HERE, and the variant is kept on its own merits: a saturated
                  fill is pre-attentive, so a pill directly under the product title
                  would compete with it for first read, and the seal is a quiet fact
                  rather than an alarm — the same judgement that put this badge in the
                  neutral zinc ramp instead of the `tampered` one. Do not restore the
                  pill on the grounds that it now has the line to itself; having the
                  line to itself is precisely why it does not need one.

                  With an explicit `icon` the `dot` branch REPLACES the dot rather than
                  adding to it, so this renders as the LockOpen glyph plus plain
                  secondary-tone label: no fill, no border, no pill.

                  The wrapper is INSIDE the condition, not around the badge — an
                  always-rendered `mt: 1` box would put 8px of dead space under the
                  title of every certificate whose seal is intact, which is most of
                  them. `display: flex` makes the badge shrink to its content so the
                  tap target is the glyph and the word, not the full page width.
                  `mt: 1` is 8px here: `apps/app` never overrides MUI's 8px base, so
                  the factor is HALF what the same number means in the platform
                  (working-conventions §29). */}
              {certificate?.is_tag_tampered && (
                <Box sx={{ mt: 1, display: 'flex' }}>
                  <TagOpenedBadge variant='dot' onClick={() => setOpenSealOpened(true)} />
                </Box>
              )}
          </Box>

          {/* VERA-315 — the state cards, ranked. This is where the pending-transfer
              card alone used to render; the missing-state card has moved UP from
              inside the access-gated block below (its `active` flag carries the
              ownership gate, so nothing is newly exposed — see `stateCards`), and
              the suspicious-activity Alert has moved DOWN from above the product
              title. All three are now one ranked stack whose baseline height is one
              card plus one row however many states apply. */}
          <StateCardStack items={stateCards} />

          {/*
            THE ACTION TILES.

            ## What this replaces, and why the replacement is not a reversal

            Until now this was ONE card labelled `Actions` with a chevron, opening
            the sheet below. Its argument — that Gift, Sell and Report are PEERS,
            so promoting any one of them teaches a hierarchy that does not exist —
            was right, and is why this is a row of IDENTICAL tiles. Nothing about
            size, colour or position ranks them.

            Where it went wrong was the conclusion it drew: it read "there is no
            primary" as grounds to promote NONE of them, so the page carried a
            control whose entire content was the word `Actions`, and every action
            — including the one you opened the certificate to take — cost a tap
            that revealed only a list. A row of peers states the relationship
            directly instead of describing it. It is the iOS contact-card idiom
            (Message / Call / Video / Mail).

            Its other argument, that a single trigger scales to N with no width
            pressure, is answered by `MAX_TILES` rather than conceded: past four
            actions the last tile becomes `More` and opens this same sheet. That
            is why the sheet is still mounted below — it is the overflow path now.

            The earlier in-page LIST that was tried and dropped is still not what
            this is: that competed with the History rows for one idiom (icon,
            label, chevron, full width) while one navigates and one mutates. A row
            of square glyph tiles shares no idiom with a navigation row, and it
            spends ~76px of page height against that list's ~150.

            Gated on `hasManageActions` exactly as the doorway was — a row of zero
            tiles must not render (working-conventions §46). `ActionTileRow` gates
            on the same emptiness internally; see the note there.
          */}
          {hasManageActions && (
            <ActionTileRow
              items={manageItems}
              onOverflow={() => setOpenOptions(true)}
            />
          )}

          {/*
            VRG-781 — there is deliberately NO standing "see what visitors see" control
            here.

            One was built (a tertiary text button) and removed: Spencer rejected a
            persistent control on the grounds of working-conventions §42, "two controls
            with one meaning", and the ticket's acceptance criterion puts the ongoing
            entry in the OPTIONS DRAWER, not on the page body. A text button in the body
            is not literally the "chrome toggle" that was rejected, but it is the same
            thing in substance — a second standing affordance beside the per-arrival
            sheet — so it goes.

            The per-scan `ViewChoiceSheet` below is the decided mechanism: every arrival
            asks, so the visitor view is never more than one tap away on the path an owner
            actually takes.

            ⚠️ The drawer that should hold the ongoing entry is currently UNREACHABLE —
            `ActionMenu` opens only from the tile row's `More` tile, which appears past
            `MAX_TILES` actions, and there are three today. So adding the item there now
            would ship a feature nobody can reach. Raised rather than worked around; see
            the task file's "no entry into the preview from the certificate itself".
          */}

          {/* THE PAYWALL GATE. The hero and title above are real and ungated; from
              here down the visitor gets the ask, and behind it the SHAPE of what the
              ask opens, blurred.

              This is the whole reason the gate is a card and not a route. A standalone
              page withholds the details AND every trace that there are details, so the
              ask lands with nothing behind it — the shape of a phishing form. Here it
              lands on the certificate, under the item's own photograph and name, over
              a page-worth of obscured content: the arrangement people already read as
              "there is something here, and this is what it costs".

              ⚠️ THE BLUR IS OVER PLACEHOLDERS, NEVER OVER THE REAL DETAILS. The public
              endpoint hands the entire certificate to an anonymous caller, so a filter
              over real text is defeated by select-all or reader mode and protects
              nothing while looking like it does. See `GatedDetailsPlaceholder`. */}
          {showClaimCard && (
            <GateSurface
              /* NEVER a sheet, whatever `?present=` says: "Claim ownership" opens a
                 DrawerPopup of its own, so a sheet here spends the §52 two-drawer
                 budget at step one. See GateSurface's docblock. */
              presentation="card"
              title={'Claim this item'}
              /* Optional, and it already owns a dismiss — so unlike the gate
                 below, the sheet's swipe and backdrop are LEFT working and map
                 onto the same outcome as "View this item". */
              onClose={() => setClaimDismissed(true)}
            >
              <ClaimOnScanCard
                certificateId={certificateId as string}
                vt={searchParams.get('vt')}
                collectEmail={accessSettings.collectEmail}
                collectLocation={accessSettings.collectLocation}
                ownerName={
                  [certificate?.customer?.first_name, certificate?.customer?.last_name]
                    .filter(Boolean)
                    .join(' ') || null
                }
                companyId={certificate?.company_id ?? null}
                onDismiss={() => setClaimDismissed(true)}
              />
            </GateSurface>
          )}

          {showAccessGate && (
            <>
              {!showClaimCard && (
              <GateSurface
                presentation={gatePresentation}
                title="Access certificate"
                /* MANDATORY. No grabber, no backdrop dismissal, no swipe — the
                   honest configuration, and the thing to judge: a sheet you
                   cannot push away may read as a stuck control rather than as a
                   gate. The card never had to answer that question. */
                mandatory
              >
              <RequestAccessInfo
                viewType={viewType}
                certificateId={certificateId as string}
                isAccessDataCollected={isAccessDataCollected}
                setIsAccessDataCollected={setIsAccessDataCollected}
                collectEmail={accessSettings.collectEmail}
                collectLocation={accessSettings.collectLocation}
                /* Only ever non-empty for a visitor whose STORED consent was found not
                   to cover the gate as it now stands (`cachedConsentVerdict`
                   → 'insufficient'), which in practice means the brand switched
                   location on since they last visited. Their email is not the part in
                   question, so re-typing it is friction for a requirement they already
                   met; the ask narrows to what actually changed. A first-time visitor
                   has no stored consent and this is `undefined`. */
                initialEmail={readAccessConsent()?.email}
                companyId={certificate?.company_id ?? undefined}
              />
              </GateSurface>
              )}
              {/* Stays put in BOTH presentations, and is the whole comparison.
                  As a card the blurred rows sit below the ask, in the open. As a
                  sheet they sit behind the backdrop, dimmed — which is the cost
                  worth looking at, since they are the thing that makes the ask
                  read as a price for something rather than a form from nowhere. */}
              <GatedDetailsPlaceholder />
            </>
          )}

          {showGatedContent && (
            <>
              {/* VERA-315: the missing-state card that stood HERE has moved into
                  the ranked `StateCardStack` above, alongside the pending-transfer
                  card it was always a sibling of and the suspicious-activity notice
                  that used to sit above the product title.

                  Its `Collapse` moved with it — the stack wraps every item in one,
                  uniformly, so "Report found" still animates the card away in place
                  rather than reloading the page (see `handleConfirmStatusChange`).
                  What changed is that the stack's Collapse does NOT `unmountOnExit`:
                  a card whose state has passed stays mounted at zero height, which
                  is what lets a card be promoted or demoted without React tearing
                  down its subtree. See `StateCardStack`'s DOM-ORDER note.

                  The access gate this block still applies was redundant for that one
                  card: `isMissing` requires `isOwnerView`, i.e. `viewType === OWNER`,
                  which already implies `viewType !== PUBLIC`. Everything else below
                  keeps the gate. */}

              {/* VERA-315 Phase 1: the `Preview public certificate` button and the
                  sharing gear that stood here are GONE, into the header's overflow
                  menu (`Preview`, `Share settings`).

                  Preview was already duplicated — the overflow has carried its own
                  `Preview` item the whole time — so the inline pair was a second
                  copy of one action plus an unlabelled icon, sitting in the flow
                  between the certificate's state and its facts. Measured at
                  375×812 it occupied y≈716 of an 812px viewport: an entire action
                  zone, above the fold, before a single certificate fact.

                  See VRG-198 in git history for what this replaced (a passive grey
                  notice card); its privacy sentence lives at the top of the Share
                  screen and is unaffected. */}

              {/* VERA-315 Phase 4: the four tabs are gone. Product was the default
                  and is now simply the page; Provenance, Scans and Activity are
                  chevron rows that push to their own screens.

                  What the tab row cost: a permanent 56px control, four labels
                  competing for 375px — the last of which truncated to "Act…" — and
                  the implication that all four are peers when one is the default
                  and the rest are occasional. Chrono24 pushes the same kind of depth
                  into `Details ›` / `Description ›` rows for the same reason.

                  The rows reuse `FieldRow` (the Profile settings row) and the
                  push transition already built for list → certificate, so this is
                  mostly assembly. */}
              {isOwnerView && (
                <ProductTabContent
                  certificate={certificate}
                  showCurrentOwner={false}
                  // Between the facts and the description, NOT after it — a long
                  // description otherwise buries the three screens this
                  // certificate can reach.
                  afterFacts={
                    /* The section rows are a LIST, and everything above them is a
                       column of content. Three signals carry that, because the
                       chevron alone was not carrying it: an icon rail the facts do
                       not have, hairlines that run edge-to-edge past the page
                       gutter, and a caption naming the group. */
                    /*
                      No `mt` here and no `first` on the label — the gap to the
                      previous section is SectionLabel's job, so all three of this
                      page's sections and Profile's four share one value.

                      This is what was actually wrong when the sections "didn't
                      look distinct": every caption on this page passed `first`
                      (mt: 0), so raising the component's gap moved nothing here.
                      The rhythm came from three unrelated parents instead, and
                      measured 16px and 24px for two instances of one component.
                    */
                    <>
                    {/* VRG-720: the contact card, when the product carries one and the owner
                        holds it. Renders nothing otherwise. */}
                    <ContactCardSection
                      certificateId={certificateId as string}
                      canRequest={canRequestContactCard}
                    />
                    <Stack>
                      <SectionLabel>History</SectionLabel>
                      {certificateSubViewSections(certificate).map(({ id, label, path, Icon, termId }) => (
                        <FieldRow
                          // `id`, not `path`: `path` is optional on a section (the
                          // inline record has none), and `id` is the registry's own
                          // stable identity.
                          key={id}
                          label={label}
                          icon={Icon ? <Icon size={20} strokeWidth={1.5} /> : undefined}
                          fullBleed
                          // The invalid-scan signal lives HERE as well as in the
                          // banner, and this copy does not dismiss: the banner is
                          // the transient attention-getter, the badge is the
                          // standing fact. A stray tap on the banner's X should
                          // not be able to silence a possible-counterfeit signal
                          // for the rest of the visit.
                          //
                          // VRG-418 — the row's `(i)` shares this slot. It is the
                          // only trailing slot `FieldRow` has, and the two never
                          // compete: the badge appears on `scans` only when there
                          // are invalid ones, which is precisely when a reader most
                          // wants to know what a scan IS. `TermInfo` stops the row's
                          // click, so tapping it defines the word instead of opening
                          // the screen.
                          meta={
                            (path === 'scans' && invalidScanCount > 0) || termId ? (
                              <Stack direction='row' alignItems='center' spacing={1}>
                                {path === 'scans' && invalidScanCount > 0 && (
                                  <StatusBadge
                                    status='stolen'
                                    label={
                                      invalidScanCount === 1
                                        ? '1 invalid'
                                        : `${invalidScanCount} invalid`
                                    }
                                  />
                                )}
                                {termId && <TermInfo termId={termId} />}
                              </Stack>
                            ) : undefined
                          }
                          // No value and no placeholder: `FieldRow`'s default
                          // placeholder is "Add", which is right for an empty
                          // settings field and nonsense on a navigation row.
                          placeholder=''
                          onClick={() => path && openSubView(path)}
                        />
                      ))}
                    </Stack>
                    </>
                  }
                />
              )}

              {/*
                THE KEBAB, AGAIN — and this time it holds only what belongs in one.

                It is worth recording why the round trip is not a reversal. The
                ORIGINAL kebab held four items, two of which were duplicates:
                `Share settings` navigated to /share, which is exactly where the
                header's Share icon goes, and `Preview` was a weaker copy of the
                Share screen's own per-view Preview. Those two are still gone, and
                are not coming back — Share reaches both in one tap.

                What is left is the two that MUTATE the certificate, and the reason
                they were pulled out into the page (a pinned Gift, then a labelled
                Manage list) was that a menu of four, half of them redundant, is a
                junk drawer. A sheet of two real, related actions is not. The set is
                also about to grow past two, which is what settles it — see
                `manageItems`.
              */}

              {!isOwnerView && (
                <>
                {/* VRG-511 — FIRST in the public branch, above the product content.
                    A past owner's question on arrival is "why does this look
                    different", so the answer has to precede the thing that looks
                    different rather than sit under it (§40's argument for putting a
                    page's qualifying copy at the top, not the bottom).

                    Renders only once the consent query has settled. Showing it
                    optimistically would flash "you previously owned this" at every
                    stranger who taps the tag for as long as the request takes. */}
                {isPastOwnerView && !isConsentLoading && (
                  <Box sx={{ mb: 3 }}>
                    <PreviouslyOwnedCard
                      sharesIdentity={provenanceConsent?.shares_identity ?? true}
                      disabled={updateProvenanceConsent.isPending}
                      onChange={next => updateProvenanceConsent.mutate(next)}
                    />
                  </Box>
                )}
                <ProductTabContent
                  certificate={certificate}
                  // VERA-302: in preview the OWNER is fetching, so the server returns
                  // the full payload — the client has to apply the view's own rule or
                  // the preview would lie about what a visitor sees. For a real public
                  // visitor the server has already stripped `customer`, so `true` is
                  // simply "render whatever survived".
                  showCurrentOwner={
                    viewType === ViewTypes.OWNER_PUBLIC_PREVIEW
                      ? Boolean(previewVisibility?.show_ownership)
                      : true
                  }
                />

                {/* VRG-720: the contact card sits with the facts, before the published
                    sections. Renders nothing unless the viewer has access AND the product
                    carries a card. */}
                <ContactCardSection
                  certificateId={certificateId as string}
                  canRequest={canRequestContactCard}
                />

                {/* VRG-782 — the brand's Experience copy, INLINE rather than behind a
                    row.

                    The owner reaches this same component by tapping an Experience row
                    (it is a section in the registry like Provenance or Scans). A public
                    visitor does not, and that asymmetry is this page's own rule, not an
                    exception to it: `PublicCertificateSections` records that the public
                    certificate is one vertical scroll precisely so nothing a brand
                    published sits "behind tabs they have no reason to know about". A
                    visitor who tapped a tag is not going to go hunting.

                    Renders nothing when the SKU has no Experience copy — the component
                    returns null on empty, so there is no heading over a blank space. */}
                {experienceHtmlPresent && (
                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 500, mb: 2 }}>
                      Experience
                    </Typography>
                    <ExperienceTabContent
                      content={certificate?.product?.experience_content}
                    />
                  </Box>
                )}

                {/* VERA-299: the published sections, in the same vertical stack — the
                    public certificate has no tabs. In preview the OWNER is fetching,
                    so the previewed view's rules decide; for a real visitor the server
                    has already applied them and this just renders what came back. */}
                <PublicCertificateSections
                  certificateId={certificateId || ''}
                  visibility={
                    viewType === ViewTypes.OWNER_PUBLIC_PREVIEW
                      ? previewVisibility
                      : publicVisibility
                  }
                  viewSlug={previewViewSlug}
                />
                </>
              )}
            </>
          )}
        </Stack>
      </Box>
      </Box>{/* record column */}
      </Box>{/* two-column body */}

      {openHowToScan && (
        <DrawerPopup
          title='How to scan?'
          child={
            <HowToScanBlock
              onAcceptTransfer={handleAcceptTransfer}
              onWatchVideo={handleOpenVideoGuide}
            />
          }
          handleOpen={openHowToScan}
          handleClose={() => setOpenHowToScan(false)}
        />
      )}

      {signIn && (
        <DrawerPopup
          title='Sign in'
          child={<SignIn height='460px' inDrawer handleClose={() => setSignIn(false)} />}
          handleOpen={signIn}
          handleClose={() => setSignIn(false)}
        />
      )}

      {!linkAccounts && (
        <DrawerPopup
          title='Link accounts'
          child={<LinkAccounts inDrawer handleClose={() => setLinkAccounts(true)} />}
          handleOpen={!linkAccounts}
          handleClose={() => setLinkAccounts(true)}
        />
      )}

      {isShowAcceptTransfer && (
        <DrawerPopup
          title='Accept transfer'
          child={
            <ClaimScanProcess
              certificate={certificate}
              closeTransfer={() => setOpenAcceptTransfer(false)}
            />
          }
          handleOpen={openAcceptTransfer}
          handleClose={() => setOpenAcceptTransfer(false)}
        />
      )}

      {isShowSuccessOwnership && certificate && (
        <DrawerPopup
          title='Accept transfer'
          child={
            <SuccessOwnership
              open={isShowSuccessOwnership}
              onClose={closeSuccessPopup}
              certificate={certificate}
            />
          }
          handleOpen={isShowSuccessOwnership}
          handleClose={closeSuccessPopup}
        />
      )}

      {/* VERA-839: the ScanClaimProcess drawer prompts an unauthenticated viewer
          to sign in / create an account to claim this certificate. It renders
          while `!claimedProduct`; closing it must therefore set the flag TRUE
          (dismissed) so it hides. The previous handlers set it FALSE, which
          re-satisfied `!claimedProduct` and re-opened the drawer on close.
          NOTE (product decision, see VERA-837 verify): there is currently no
          trigger that sets `claimedProduct=false`, so this drawer never opens.
          Wire the open trigger (e.g. `!isAuthenticated` on a claimable cert)
          once the claim-entry UX is confirmed. */}
      {!claimedProduct && (
        <DrawerPopup
          title='Sign In'
          child={<ScanClaimProcess handleClose={() => setClaimedProduct(true)} />}
          handleOpen={!claimedProduct}
          handleClose={() => setClaimedProduct(true)}
        />
      )}

      {openTransferOwnership && isAuthenticated && (
        <DrawerPopup
          title='Transfer ownership'
          child={
            <TransferOwnership
              onExpired={() => setIsExpired(true)}
              certificate={certificate}
              closeTransfer={() => setOpenTransferOwnership(false)}
            />
          }
          handleOpen={openTransferOwnership}
          handleClose={() => setOpenTransferOwnership(false)}
        />
      )}

      {openVideoGuide && <VideoPlayer handleClose={() => setOpenVideoGuide(false)} />}

      {/*
        No `canVerify` prop. This branch threaded the capability into the drawer so it
        could pick its own copy; main's VRG-252 reaches the same place through the
        SETTLED VERDICT instead — `verified='connected'` already means "a Connect tag
        was tapped and cannot answer a challenge", which is the only thing the drawer
        needed the capability for. One channel, not two.
      */}
      <AuthenticityDrawer
        open={openAuthenticity}
        onClose={() => setOpenAuthenticity(false)}
        // VRG-252 — the settled verdict, not a boolean collapse. `scanVerified === 'verified'`
        // mapped a Connect tap to the UNVERIFIED drawer, whose copy tells the reader to go tap the
        // tag they had just tapped, and whose "how authentication works" paragraph describes a
        // per-tap cryptographic proof this tag does not have. `pending` still reads as unverified
        // here, which is right: the drawer is only reachable by tapping the chip, and by then the
        // resolve has settled.
        verified={
          scanVerified === 'verified' || scanVerified === 'connected' ? scanVerified : 'unverified'
        }
        // Same source the Footer reads, so the two agree. A white-labelled brand
        // that hides the footer attribution must not meet it again inside a drawer.
        hidePoweredBy={isPreview ? previewHidePoweredBy : certificate?.hide_powered_by}
        /*
          VERA-304 — the badge becomes a route to the possession scan, not just an
          explanation of itself. The SAME predicate the `Verify` tile is gated on,
          passed verbatim: two routes to one action, so a divergence here would let
          one of them offer something the other has already withdrawn. It is also
          what makes this self-clearing — the mutation invalidates the detail query,
          `completion_method` comes back `scan_claimed`, and the block and the tile
          disappear together.

          CLOSE-THEN-OPEN, in that order, matching `ActionMenu.handleItemClick`
          (working-conventions §52). The two state updates batch, so this drawer
          starts leaving in the same frame `ScanTag` starts arriving and the DOM
          never holds two papers. An explainer is not a step to scan on top of.
        */
        canVerifyPossession={canVerifyPossession}
        onVerifyPossession={() => {
          setOpenAuthenticity(false);
          setOpenVerifyPossession(true);
        }}
      />

      <SealOpenedDrawer
        open={openSealOpened}
        onClose={() => setOpenSealOpened(false)}
        tagOpenedAt={certificate?.tag_opened_at}
        supportEmail={certificate?.company?.support_email}
        supportPhone={certificate?.company?.support_phone}
      />


      {/*
        The MANAGE sheet — now the OVERFLOW path, not the main one.

        CONTROLLED, and the trigger is the tile row's `More` tile (the header kebab
        it originally named is gone), so `ActionMenu` renders no trigger of its own
        and `openOptions` is the only thing that opens it.

        ⚠️ It is currently UNREACHABLE, and that is the correct state rather than
        dead code: `More` only appears past `MAX_TILES` actions and there are three
        today. It stays mounted so the fifth action lands with its overflow already
        working, and because it renders the same `manageItems` array the tiles do —
        there is nothing here to fall out of step with.

        ⚠️ working-conventions §52 (never three drawers): this sheet MUST be gone
        before the action it launched appears, or `kebab → Gift → ScanTag` is a
        three-level stack. `ActionMenu.handleItemClick` closes first and calls
        `onClick` second, in that order, which is what makes this safe — the two
        state updates batch, so the sheet starts leaving in the same frame the next
        surface starts arriving and the DOM never holds three papers. Verified by
        counting `.MuiDrawer-paper` / `.MuiDialog-paper` through both routes: max 2.
      */}
      {/*
        TITLED FOR THE TRIGGER THAT OPENS IT. The header button is labelled
        "Actions"; this sheet said "Manage", so pressing a control named one thing
        opened a surface named another — the reader has to re-establish that they
        are in the right place, and the two words also gave the same concept two
        names in one flow. The certificates list's own overflow sheet was already
        titled "Actions" (`MoreOptionsMenu`), so "Manage" was the odd one out of
        three.

        It matters more than a label usually would because this sheet has no other
        anchor: it opens centred at md+, detached from the button, so its title is
        the only thing tying it back to what was pressed. Same principle the
        Layout button follows in `HeaderBar` — a visible label and a differently
        named target is a mismatch, and here the "target" is a whole surface.
      */}
      <ActionMenu
        presentation='sheet'
        title='Actions'
        open={openOptions}
        onClose={() => setOpenOptions(false)}
        items={manageItems}
      />

      {/* VRG-781 — the per-scan arrival choice. Dismissal (X, backdrop, swipe) resolves
          to the owner view, which is already the state underneath, so there is no dead
          end and no special case. See ViewChoiceSheet for why it asks every time. */}
      <ViewChoiceSheet
        open={openViewChoice}
        onChooseOwner={() => setOpenViewChoice(false)}
        onChooseVisitor={() => {
          setOpenViewChoice(false);
          setViewType(ViewTypes.OWNER_PUBLIC_PREVIEW);
        }}
      />

      <ReportMissingDrawer
        open={openReportMissing}
        onClose={() => setOpenReportMissing(false)}
        certificateId={certificate?.id}
        onViewCertificate={() => {
          setOpenReportMissing(false);
        }}
        onShare={handleShare}
        onStatusChange={() => {
          if (certificateId)
            queryClient.invalidateQueries({ queryKey: ['certificate-public', certificateId] });
        }}
      />

      {/* VERA-304 (A1) — "Verify possession".

          REUSES `ScanTag` rather than adding a second scan surface: it already
          owns the scan interaction, the brand's "Where to scan" content, the
          pulse/success animation and the dev affordance. A parallel sheet would
          have to keep all of that in step and would drift on the first change.

          §52 (never three stacked drawers) is satisfied by construction: this
          opens from a TILE on the page, so at most one sheet is ever on screen,
          and its terminal state renders IN PLACE via `successTitle` rather than
          opening a confirmation on top of it. The `More` overflow sheet is the
          only other route in, and `ActionMenu` closes itself before invoking
          `onClick` — the same ordering that makes kebab -> Gift -> ScanTag safe.

          Both scan paths — the real `?token=` from an NFC redirect and the dev
          mint — go through `submitPossessionScan`, so the success screen can
          only appear for a request the SERVER accepted. */}
      {certificate && (
        <ScanTag
          title='Verify possession'
          pageTitle='Scan to verify possession'
          scanInstructions={certificate.product?.scan_instructions}
          scanMediaUrl={certificate.product?.scan_media_url}
          open={openVerifyPossession}
          onClose={() => setOpenVerifyPossession(false)}
          onScanToken={submitPossessionScan}
          onSimulateScan={async () => {
            const token = await mintDevScanToken(certificate.id);
            return token ? submitPossessionScan(token) : false;
          }}
          successIcon={<BadgeCheck size={40} />}
          successTitle='Possession verified'
          successDescription='This item now shows as scan verified in its provenance. Anyone viewing the certificate can see the physical tag was checked by its owner.'
          successActionLabel='Done'
          successActionOnClick={() => setOpenVerifyPossession(false)}
        />
      )}

      {certificate && (
        <DrawerPopup
          title='Gift'
          child={
            <GiftCertificate
              certificate={certificate}
              closeGift={() => setOpenGiftCertificate(false)}
            />
          }
          handleOpen={openGiftCertificate}
          handleClose={() => setOpenGiftCertificate(false)}
        />
      )}

      {/*
        The SELL sheet — the screens of selling, without the writes.

        `SellCertificate` carries VERA-632's own steps: the method chooser, then
        either the listing step (asking price + public-visibility toggle) or the
        P2P offer form (recipient + price + show-ownership) followed by the
        in-person/ship choice. Only the final write is absent — that step says so
        and names what did not happen, rather than firing a request against an
        endpoint `main` does not have.

        One sheet, swapping its body between steps — §52, two drawers is the
        maximum and anything deeper would be the third over this page. The one
        nested drawer is the recipient picker, which is level two.

        `key` remounts it per open so a seller who closes mid-flow and taps Sell
        again starts at the chooser rather than resuming someone else's half-filled
        form.
      */}
      {certificate && (
        <DrawerPopup
          title='Sell'
          child={
            <SellCertificate
              key={sellOpenCount}
              certificate={certificate}
              onClose={() => setOpenSellMethod(false)}
            />
          }
          handleOpen={openSellMethod}
          handleClose={() => setOpenSellMethod(false)}
        />
      )}

      {/* VERA-301: a BOTTOM DRAWER, not a Dialog. Standing rule — confirmations
          in apps/app are bottom sheets; this page's other confirms (Report
          missing, options, share) already are, and a centred modal on a phone
          is both an outlier and further from the thumb. `DrawerPopup` defaults
          to anchor='bottom' and content-height (VERA-842), so it sizes to these
          two lines instead of filling the viewport.

          Both entry points (lost and stolen) are the SAME action and share the
          "Report found" label, so nothing here branches on status. The old
          non-stolen copy also claimed the change "cannot be undone", which is
          false — an item can be reported missing again. */}
      <DrawerPopup
        title='Report found'
        handleOpen={openConfirmStatusChange}
        handleClose={() => setOpenConfirmStatusChange(false)}
        child={
          <Stack spacing={1.5} pb={1}>
            <Typography
              sx={{ fontSize: font.body, fontWeight: 400, color: 'text.secondary', lineHeight: 1.5 }}
            >
              This marks the item as recovered and sets the certificate back to active. You can
              report it missing again at any time.
            </Typography>
            <Stack spacing={1.5} pt={1}>
              <Button
                variant='contained'
                fullWidth
                onClick={handleConfirmStatusChange}
                sx={{
                  backgroundColor: theme => theme.palette.common.black,
                  color: theme => theme.palette.common.white,
                  // VRG-562: a button — the raw brand radius, as every other button.
                  borderRadius: innerRadius,
                  textTransform: 'none',
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: theme => theme.palette.grey[800],
                  },
                }}
              >
                Report found
              </Button>
              <Button
                variant='outlined'
                fullWidth
                onClick={() => setOpenConfirmStatusChange(false)}
                sx={{
                  borderColor: theme => theme.palette.grey[300],
                  color: theme => theme.palette.text.primary,
                  backgroundColor: theme => theme.palette.common.white,
                  // VRG-562: a button — the raw brand radius, as every other button.
                  borderRadius: innerRadius,
                  textTransform: 'none',
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: theme => theme.palette.grey[50],
                  },
                }}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        }
      />
      {/* Never sticky here any more. The rule this replaces — sticky on the Scans
          LIST and the Activity timeline, because those page infinitely — still
          holds, but both moved to their own screens in Phase 4 and each now owns
          it (see the sub-views). This page ends, so pinning the attribution would
          only cost viewport. */}
      {/* `compact` only while the gate is up: the attribution's usual 28px of clearance
          is measured against PROSE, and the gated view has none above it — a bordered
          card with its own 16px margin, then a placeholder allowed to collapse to zero.
          See `FooterProps.compact`. */}
      <Footer
        hidePoweredBy={isPreview ? previewHidePoweredBy : certificate?.hide_powered_by}
        compact={showAccessGate}
      />
    </Stack>
    </ContentFade>
    </>
  );
};
